// src/pages/DepartmentReview.jsx
//
// Department Review — HOD-facilitated capture, admin-only analysis.
//
// ─────────────────────────────────────────────────────────────────────────
// What changed in this redesign (v2):
//
//   1. HODs never see an AI summary. They capture freely across six steps
//      (typed notes, audio recordings, capability taps) and tap "Complete &
//      Submit" on Step 6. That tap fires-and-forgets a single session-level
//      analysis call via navigator.sendBeacon — the HOD sees an instant
//      "Thank you" and the browser delivers the request even if they close
//      the tab immediately.
//
//   2. One summary per department, not six per-step summaries. On submit,
//      all six segments are compiled into a single deterministic blob and
//      a single LLM call analyzes the whole session holistically. This
//      lets the model connect Step 2's growth area to Step 5's capability,
//      see the department's overall arc, etc.
//
//   3. Summaries are stored and reused. Regeneration only happens on an
//      explicit admin button press. The compiled session is the persistent
//      source of truth; the summary is derived and replaceable.
//
//   4. Admin-only views:
//        - Per-department detail view (read summary, regenerate, export PDF)
//        - At-a-glance roll-up (existing synthesis + chat from v1)
//        - PII audit (superadmin ONLY — shows original-vs-scrubbed for
//          every LLM call, so the scrubber's misses can be monitored)
//
//   5. Data layer kept generic for a future upload-content tool — analyses
//      live in a `pdInputs` collection keyed by source, so the same admin
//      surfaces can later display uploaded-document analyses next to
//      department-review analyses without a refactor.
//
// Data model:
//
//   /departmentReviews/{deptId}
//     - departmentId, departmentName, status: 'in-progress' | 'submitted'
//     - submittedAt, submittedBy (uid), compiledSession (the deterministic
//       blob — all six steps' notes + transcripts + taps, the source of
//       truth that summaries are derived from)
//     - summaryStatus: 'pending' | 'ready' | 'failed'
//     - summaryStatusMessage (when failed)
//
//   /departmentReviews/{deptId}/segments/{stepId}    ← HOD's per-step capture
//     - notes, mode, temps, transcript, audioStatus
//     (No per-segment AI summary anymore. `audioStatus` here just tracks
//      the transcription job, not any LLM call.)
//
//   /pdInputs/{inputId}                              ← admin-facing analysis
//     - source: 'department-review'  (other sources later, e.g. 'upload')
//     - sourceRef: e.g. 'departmentReviews/stem'    (back-pointer)
//     - departmentName, createdAt, generatedAt, status: 'ready' | 'failed'
//     - summary: the structured AI output (themes, blockers, etc.)
//     - failureReason (when status === 'failed')
//
//   /piiAuditLog/{logId}                             ← superadmin ONLY
//     - source, sourceRef, originalText, scrubbedText, sentAt
//
// Backend reuse / additions:
//   - audio-uploads/{uid}/...  + processAudioFile   (existing, unchanged)
//   - generateSessionSummary   (NEW — inline in functions/index.js)
//     Receives compiledSession, runs scrubber, writes audit log + pdInputs
//     doc + flips summaryStatus on the parent review doc.
//
// ─────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useRef, useCallback } from "react";
import { getAuth } from "firebase/auth";
import { getStorage, ref, uploadBytesResumable } from "firebase/storage";
import {
  doc, getDoc, setDoc, deleteDoc, collection, getDocs, query, where, orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { jsPDF } from "jspdf";
import JSZip from "jszip";
import { db } from "../config/firebase";
import { useAuth } from "../contexts/AuthContext";

const FUNCTIONS_BASE_URL = "https://us-central1-kings-app-74847.cloudfunctions.net";

// ── KA brand ─────────────────────────────────────────────────────────────
const KA = {
  red: "#E83530",
  navy: "#233C5F",
  gold: "#A29164",
  paper: "#FBFAF7",
  ink: "#1C2530",
  line: "#E4E0D6",
  mute: "#6B7280",
};

// ── Departments ──────────────────────────────────────────────────────────
const DEPARTMENTS = [
  { id: "history",         name: "History" },
  { id: "stem",            name: "STEM" },
  { id: "english",         name: "English" },
  { id: "arabic",          name: "Arabic" },
  { id: "world-languages", name: "World Languages" },
  { id: "arts",            name: "Arts" },
];

// ── Capability examples per department ──────────────────────────────────
const CAPABILITY_EXAMPLES = {
  history: [
    { id: "h1", title: "Source differentiation",
      body: "AI can take a single primary source — a 1920s political speech, a treaty excerpt — and instantly produce three reading-level versions plus a glossary, so the same document works for an entire mixed-ability class.",
      artifact: null },
    { id: "h2", title: "The adversarial historian",
      body: "A student can have AI role-play a historical figure defending their actions, then the student has to cross-examine it and catch the anachronisms — turning AI into a debate opponent rather than an answer machine.",
      artifact: null },
    { id: "h3", title: "Bias detector as the assignment",
      body: "Instead of asking AI to summarize an event, students ask it to summarize the same event 'as a nationalist textbook would' versus 'as the opposing side would', then analyze the differences — making AI's tendency toward framing into the actual lesson.",
      artifact: null },
  ],
  stem: [
    { id: "s1", title: "Worked-solution critique",
      body: "AI can generate a solution to a problem that contains a deliberate, plausible error, and students have to find and explain it — assessing reasoning instead of answer-getting.",
      artifact: null },
    { id: "s2", title: "Instant lab-data variation",
      body: "AI can generate realistic but distinct datasets for every lab group, so analysis is genuinely individual and conclusions can't be copied between groups.",
      artifact: null },
    { id: "s3", title: "Misconception probing",
      body: "Before teaching a unit, a teacher can ask AI to list the ten most common student misconceptions about a topic — say, Newton's third law — and design the lesson to pre-empt them.",
      artifact: null },
  ],
  english: [
    { id: "e1", title: "Feedback on the process, not the product",
      body: "AI can review a student's draft against a rubric and give targeted revision questions — 'where is your evidence weakest?' — rather than a polished rewrite, coaching revision instead of replacing it.",
      artifact: null },
    { id: "e2", title: "Reverse-outlining",
      body: "Students paste their own finished essay and AI reconstructs its argument structure, revealing whether the logic they intended actually came through on the page.",
      artifact: null },
    { id: "e3", title: "Style imitation as craft study",
      body: "AI can rewrite a neutral paragraph in the voice of an author the class is studying, giving students a concrete object to analyze: what specific choices make that author sound like themselves?",
      artifact: null },
  ],
  arabic: [
    { id: "a1", title: "Register and dialect shifting",
      body: "AI can take a Modern Standard Arabic passage and render it in colloquial dialect, or shift formality levels, making the diglossia of Arabic visible and teachable rather than abstract.",
      artifact: null },
    { id: "a2", title: "Vocalization and reading support",
      body: "AI can add full tashkeel to an unvocalized text and generate comprehension questions at graded difficulty, scaffolding readers at different levels from one source.",
      artifact: null },
    { id: "a3", title: "Authentic prompt generation",
      body: "AI can produce culturally grounded writing prompts and model responses in Arabic, giving teachers a starting bank rather than building every prompt from scratch.",
      artifact: null },
  ],
  "world-languages": [
    { id: "w1", title: "Conversation partner on demand",
      body: "AI can hold a spoken or written conversation at a set proficiency level, letting students practice low-stakes dialogue with infinite patience outside class.",
      artifact: null },
    { id: "w2", title: "Error-pattern analysis",
      body: "A teacher can paste a set of student writing samples and AI identifies the recurring grammar errors across the whole class, pointing directly at what to reteach.",
      artifact: null },
    { id: "w3", title: "Comprehensible-input generation",
      body: "AI can rewrite any authentic text to a target proficiency level while keeping it culturally real, solving the perennial scarcity of level-appropriate material.",
      artifact: null },
  ],
  arts: [
    { id: "ar1", title: "Critique partner for works in progress",
      body: "A student can photograph a half-finished painting or upload a recording of their guitar piece and ask AI to respond as a thoughtful critic — pointing at what's working, what feels unresolved, what an audience might miss. The student decides whether to take the note. Useful when no peer or teacher is free, and especially in the long stretches between formal critiques.",
      artifact: null },
    { id: "ar2", title: "Historical and stylistic context on demand",
      body: "A ceramics student studying a particular glaze, a pianist learning a Debussy piece, an orchestra preparing a Bartók movement — any of them can ask AI for the historical and technical context in seconds: what era, what was happening around it, what choices define the style, what to listen or look for. Lets students enter the work informed rather than waiting for a teacher to lecture the context.",
      artifact: null },
    { id: "ar3", title: "Generative drafting as a starting point, not a finished work",
      body: "AI can generate visual references, melodic ideas, color palettes, lyric sketches — material the student then critiques, modifies, or deliberately departs from. Reframes AI as the awkward first draft the student improves, rather than the polished output that replaces their work. Especially useful for breaking blank-canvas paralysis at the start of a project.",
      artifact: null },
  ],
};

const TEMP_OPTIONS = [
  { id: "never",   label: "Never heard of it" },
  { id: "heard",   label: "Heard of it" },
  { id: "tried",   label: "Tried it" },
  { id: "regular", label: "Use it regularly" },
];

const STEPS = [
  { id: "strengths",    kind: "discussion", minutes: 7,  title: "Curriculum strengths",
    prompt: "What are areas of strength within our curriculum and course of study? Consider the skills, concepts, and content being taught." },
  { id: "growth",       kind: "discussion", minutes: 7,  title: "Growth areas",
    prompt: "Where are the opportunities for improvement? For each, name the key driver and the key stakeholders." },
  { id: "homework-now", kind: "discussion", minutes: 6,  title: "Homework today",
    prompt: "What are our current homework practices and expectations across the department's classes?" },
  { id: "ai-inventory", kind: "discussion", minutes: 8,  title: "AI inventory",
    prompt: "Round the room: name one way — however small — you have used an AI tool for anything school-related. (Low stakes. The goal is just to find the floor.)" },
  { id: "capabilities", kind: "capability", minutes: 20, title: "Capability reactions",
    prompt: "For each capability below: read it to the room (or show the screen), run a short discussion, then tap the room's overall familiarity. Capture the discussion as you go." },
  { id: "pivot",        kind: "discussion", minutes: 10, title: "Final reflections",
    prompt: "Two questions. First: if a student can get help to do one of our current assignments in 30 seconds, what is that assignment actually for? Second: given everything today, what is the ONE thing this department most needs support with?" },
];

// ── Small helpers ────────────────────────────────────────────────────────
function useDebouncedCallback(fn, delay) {
  const timer = useRef(null);
  const fnRef = useRef(fn);
  useEffect(() => { fnRef.current = fn; }, [fn]);
  return useCallback((...args) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => fnRef.current(...args), delay);
  }, [delay]);
}

// True if a segment has any captured content (typed notes, recorded audio,
// or capability taps). Used to color the progress rail and as the soft
// "has anything happened here?" check.
function segmentHasContent(seg) {
  if (!seg) return false;
  if (typeof seg.notes === "string" && seg.notes.trim()) return true;
  if (seg.notes && typeof seg.notes === "object" &&
      Object.values(seg.notes).some((v) => typeof v === "string" && v.trim())) {
    return true;
  }
  if (seg.transcript && seg.transcript.trim()) return true;
  if (seg.temps && Object.keys(seg.temps).length > 0) return true;
  return false;
}

// ── Per-step completion rules ────────────────────────────────────────────
//
// A step is "complete" when the HOD has ticked the Mark Step Complete
// checkbox, which is only enabled when the step's content rules are met.
// Rules vary by step kind:
//
//   Discussion steps (1, 2, 3, 4, 6):
//     - Either typed notes OR a finished audio transcript. Either is enough.
//
//   Capability step (5) — "Mixed" mode:
//     - Room familiarity tapped on ALL three capability cards.
//     - PLUS either one combined audio transcript covering the whole
//       capability discussion, OR per-card typed notes on every card.
//
// Returns { isComplete, canComplete, missing[] }
//   isComplete   — has the HOD ticked Mark Complete? (segment.completedAt set)
//   canComplete  — would the checkbox be enabled right now? (all rules met)
//   missing      — human-readable list of unmet rules, for display
function getStepCompletion(step, segment, deptId) {
  const seg = segment || {};
  const isComplete = !!seg.completedAt;

  if (step.kind === "capability") {
    const examples = CAPABILITY_EXAMPLES[deptId] || [];
    const temps = seg.temps || {};
    const notes = (seg.notes && typeof seg.notes === "object") ? seg.notes : {};
    const transcript = (typeof seg.transcript === "string" ? seg.transcript : "").trim();

    const missing = [];

    // Rule 1: every capability has a familiarity tap.
    const untappedTitles = examples
      .filter((ex) => !temps[ex.id])
      .map((ex) => ex.title);
    if (untappedTitles.length > 0) {
      missing.push(
        untappedTitles.length === examples.length
          ? "Tap a room familiarity rating on each capability"
          : `Tap a familiarity rating on: ${untappedTitles.join(", ")}`
      );
    }

    // Rule 2: combined audio, OR per-card notes covering every card.
    const hasCombinedAudio = transcript.length > 0;
    const cardsWithoutNotes = examples
      .filter((ex) => !(notes[ex.id] && notes[ex.id].trim()))
      .map((ex) => ex.title);
    const hasFullPerCardNotes = cardsWithoutNotes.length === 0 && examples.length > 0;

    if (!hasCombinedAudio && !hasFullPerCardNotes) {
      if (cardsWithoutNotes.length === examples.length) {
        missing.push("Add notes under each capability, OR record one audio covering the whole discussion");
      } else {
        missing.push(`Add notes on: ${cardsWithoutNotes.join(", ")} — OR record one audio covering the whole discussion`);
      }
    }

    return { isComplete, canComplete: missing.length === 0, missing };
  }

  // Discussion step — typed notes OR audio transcript.
  const typedNotes = (typeof seg.notes === "string" ? seg.notes : "").trim();
  const transcript = (typeof seg.transcript === "string" ? seg.transcript : "").trim();
  const hasAny = typedNotes.length > 0 || transcript.length > 0;
  return {
    isComplete,
    canComplete: hasAny,
    missing: hasAny ? [] : ["Type notes OR record audio for this step"],
  };
}

// ═════════════════════════════════════════════════════════════════════════
// Main component
// ═════════════════════════════════════════════════════════════════════════
export default function DepartmentReview() {
  const { user, isSuperAdmin, department, departmentReviewAccess, devTools } = useAuth();

  // Admin = anyone who can see all departments. SuperAdmin is admin + audit.
  const isAdmin = isSuperAdmin || departmentReviewAccess === "all";
  // Dev tools — separate flag on the user doc. Independent of role.
  // Set in Firestore manually (devTools: true on /users/{uid}). Used by
  // Kyle on a non-admin test account to switch departments at will and to
  // see the PII audit. Superadmin also gets dev-tools access automatically.
  const hasDevTools = !!devTools || isSuperAdmin;
  const pinnedDeptId = department || null;

  // View routing
  const [view, setView] = useState(isAdmin ? "admin-home" : "session");
  const [activeDeptId, setActiveDeptId] = useState(pinnedDeptId);

  // The "no department" guard only fires for non-admin users without a
  // department AND without dev-tools (dev-tools users can self-assign).
  if (!isAdmin && !pinnedDeptId && !hasDevTools) {
    return (
      <Shell>
        <div style={{ ...card, textAlign: "center", padding: "48px 32px" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: KA.navy, marginBottom: 8 }}>
            No department assigned
          </div>
          <div style={{ fontSize: 13, color: KA.mute, lineHeight: 1.6 }}>
            This tool needs a department on your faculty profile. Ask an
            administrator to set the <code>department</code> field on your user
            record.
          </div>
        </div>
      </Shell>
    );
  }

  // ── Dev tools (any user with devTools: true OR superadmin) ─────────────
  if (view === "dev-tools") {
    return (
      <DevTools
        currentUid={user?.uid}
        currentDepartment={department}
        onBack={() => setView(isAdmin ? "admin-home" : "session")}
        onOpenAudit={() => setView("admin-audit")}
      />
    );
  }

  // ── Admin views ────────────────────────────────────────────────────────
  if (isAdmin) {
    if (view === "admin-home") {
      return (
        <AdminHome
          hasDevTools={hasDevTools}
          onOpenDepartment={(id) => { setActiveDeptId(id); setView("admin-department"); }}
          onOpenRollup={() => setView("admin-rollup")}
          onOpenDevTools={() => setView("dev-tools")}
        />
      );
    }
    if (view === "admin-department") {
      return (
        <AdminDepartmentDetail
          departmentId={activeDeptId}
          onBack={() => setView("admin-home")}
        />
      );
    }
    if (view === "admin-rollup") {
      return <Rollup onBack={() => setView("admin-home")} />;
    }
    if (view === "admin-audit") {
      return <PIIAudit onBack={() => setView(hasDevTools ? "dev-tools" : "admin-home")} />;
    }
  }

  // HOD without a department AND with devTools — land them in dev-tools so
  // they can self-assign.
  if (!isAdmin && !pinnedDeptId && hasDevTools) {
    return (
      <DevTools
        currentUid={user?.uid}
        currentDepartment={department}
        onBack={null}
        onOpenAudit={() => setView("admin-audit")}
        hodLanding
      />
    );
  }

  // ── HOD view — session capture ─────────────────────────────────────────
  return (
    <SessionRunnerWithDevToolsFooter
      deptId={pinnedDeptId}
      userUid={user?.uid}
      hasDevTools={hasDevTools}
      onOpenDevTools={() => setView("dev-tools")}
    />
  );
}

// Tiny wrapper — adds a discreet dev-tools link under the session screen
// for anyone with devTools: true. The session experience itself is unchanged.
function SessionRunnerWithDevToolsFooter({ deptId, userUid, hasDevTools, onOpenDevTools }) {
  return (
    <>
      <SessionRunner deptId={deptId} userUid={userUid} />
      {hasDevTools && (
        <div style={{
          position: "fixed", bottom: 16, right: 16, zIndex: 50,
        }}>
          <button
            onClick={onOpenDevTools}
            style={{
              background: KA.ink, color: "#fff", border: "none",
              borderRadius: 999, padding: "8px 14px", fontSize: 11,
              fontFamily: "inherit", fontWeight: 700, cursor: "pointer",
              letterSpacing: "0.08em", opacity: 0.85,
              boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
            }}
          >
            🛠 DEV TOOLS
          </button>
        </div>
      )}
    </>
  );
}

// Return the bare SessionRunner export for backwards compat with the
// earlier code references — and keep the actual main export above untouched.

// ═════════════════════════════════════════════════════════════════════════
// Session Runner — HOD-facing capture flow
// ═════════════════════════════════════════════════════════════════════════
function SessionRunner({ deptId, userUid }) {
  const dept = DEPARTMENTS.find((d) => d.id === deptId);
  const [stepIndex, setStepIndex] = useState(0);
  const step = STEPS[stepIndex];

  const [segments, setSegments] = useState({});
  const [parentMeta, setParentMeta] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved
  const [submitDialog, setSubmitDialog] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [segSnap, parentSnap] = await Promise.all([
          getDocs(collection(db, "departmentReviews", deptId, "segments")),
          getDoc(doc(db, "departmentReviews", deptId)),
        ]);
        const data = {};
        segSnap.forEach((d) => { data[d.id] = d.data(); });
        setSegments(data);
        setParentMeta(parentSnap.exists() ? parentSnap.data() : null);
      } catch (e) {
        console.error("Load session failed:", e);
      } finally {
        setLoaded(true);
      }
    })();
  }, [deptId]);

  const writeSegment = useCallback(async (stepId, patch) => {
    setSaveState("saving");
    try {
      const merged = { ...(segments[stepId] || {}), ...patch };
      await setDoc(
        doc(db, "departmentReviews", deptId, "segments", stepId),
        { ...merged, stepId, updatedAt: serverTimestamp() },
        { merge: true }
      );
      await setDoc(
        doc(db, "departmentReviews", deptId),
        {
          departmentId: deptId,
          departmentName: dept.name,
          status: parentMeta?.status === "submitted" ? "submitted" : "in-progress",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 1500);
    } catch (e) {
      console.error("Save segment failed:", e);
      setSaveState("idle");
    }
  }, [deptId, dept, segments, parentMeta]);

  const debouncedWrite = useDebouncedCallback(writeSegment, 900);

  const updateSegment = (stepId, patch, immediate = false) => {
    setSegments((prev) => ({ ...prev, [stepId]: { ...(prev[stepId] || {}), ...patch } }));
    if (immediate) writeSegment(stepId, patch);
    else debouncedWrite(stepId, patch);
  };

  // ── Empty-step warning + submit ──────────────────────────────────────
  // A step counts as "incomplete" if the HOD hasn't ticked Mark Complete.
  // (We still surface this in the submit dialog, but it's overridable.)
  const incompleteSteps = STEPS.filter(
    (s) => !getStepCompletion(s, segments[s.id], deptId).isComplete
  );

  const performSubmit = async () => {
    // 1. Compile the session deterministically (no AI). This is the source
    //    of truth — must succeed in Firestore before any background work.
    const compiledSession = compileSession(deptId, dept.name, segments);

    await setDoc(
      doc(db, "departmentReviews", deptId),
      {
        departmentId: deptId,
        departmentName: dept.name,
        status: "submitted",
        submittedAt: serverTimestamp(),
        submittedBy: userUid || null,
        compiledSession,
        summaryStatus: "pending",
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    // 2. Silently fire the analysis. We use fetch() with keepalive:true,
    //    NOT navigator.sendBeacon. sendBeacon silently drops requests when
    //    the content type triggers a CORS preflight — and our Cloud Function
    //    endpoint does trigger preflight (application/json + auth header).
    //    keepalive:true gives us the same "browser delivers it even if the
    //    tab closes" guarantee without the silent-drop pitfall, and lets us
    //    keep the bearer token so the function authenticates normally.
    //
    //    We do NOT await this — it's fire-and-forget. The HOD sees the
    //    thank-you instantly.
    try {
      const auth = getAuth();
      const fbUser = auth.currentUser;
      const idToken = fbUser ? await fbUser.getIdToken() : null;

      fetch(`${FUNCTIONS_BASE_URL}/generateSessionSummary`, {
        method: "POST",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        },
        body: JSON.stringify({ departmentId: deptId }),
      }).catch((e) => {
        // Failures here are non-fatal — the compiled session is saved and
        // an admin can regenerate from the admin view. HOD never knows.
        console.warn("Background analysis dispatch failed:", e);
      });
    } catch (e) {
      console.warn("Background analysis setup failed:", e);
    }

    // 3. Show the thank-you. No AI mentioned. No spinner. Just done.
    setSubmitted(true);
  };

  if (!loaded) {
    return <Shell><div style={{ ...card, textAlign: "center", color: KA.mute }}>Loading…</div></Shell>;
  }

  if (submitted || parentMeta?.status === "submitted") {
    return <ThankYou departmentName={dept.name} />;
  }

  const isLastStep = stepIndex === STEPS.length - 1;

  return (
    <Shell>
      <Header
        title={`Department Review — ${dept.name}`}
        subtitle={`Step ${stepIndex + 1} of ${STEPS.length} · roughly ${step.minutes} min`}
        right={<SaveBadge state={saveState} />}
      />

      <StepRail steps={STEPS} current={stepIndex} segments={segments} onJump={setStepIndex} />

      <div style={{ ...card, marginTop: 16 }}>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginBottom: 8, gap: 12, flexWrap: "wrap",
        }}>
          <div style={{
            fontSize: 11, fontWeight: 700, letterSpacing: "0.12em",
            color: KA.gold, textTransform: "uppercase",
          }}>
            {step.title}
          </div>
          {(() => {
            const c = getStepCompletion(step, segments[step.id], deptId);
            if (!c.isComplete) return null;
            return (
              <span style={{
                fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 999,
                background: "#E7F3EC", color: "#1B7A3D",
              }}>
                ✓ Step complete
              </span>
            );
          })()}
        </div>
        <div style={{ fontSize: 15, color: KA.ink, lineHeight: 1.6, marginBottom: 18 }}>
          {step.prompt}
        </div>

        {step.kind === "discussion" ? (
          <DiscussionStep
            deptId={deptId} dept={dept} step={step}
            segment={segments[step.id] || {}}
            onUpdate={(patch, immediate) => updateSegment(step.id, patch, immediate)}
          />
        ) : (
          <CapabilityStep
            deptId={deptId} dept={dept} step={step}
            segment={segments[step.id] || {}}
            onUpdate={(patch, immediate) => updateSegment(step.id, patch, immediate)}
          />
        )}

        <StepCompletionControl
          step={step} deptId={deptId}
          segment={segments[step.id] || {}}
          onToggle={(complete) => updateSegment(step.id, {
            completedAt: complete ? new Date().toISOString() : null,
          }, true)}
        />
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 18, gap: 12 }}>
        <button
          onClick={() => stepIndex > 0 && setStepIndex(stepIndex - 1)}
          disabled={stepIndex === 0}
          style={{ ...ghostBtn, opacity: stepIndex === 0 ? 0.4 : 1 }}
        >
          ← Previous
        </button>
        {!isLastStep ? (
          <button onClick={() => setStepIndex(stepIndex + 1)} style={primaryBtn}>
            Next step →
          </button>
        ) : (
          <button
            onClick={() => setSubmitDialog(true)}
            style={{ ...primaryBtn, background: KA.navy }}
          >
            ✓ Complete & Submit
          </button>
        )}
      </div>

      <FacilitatorTip />

      {submitDialog && (
        <SubmitDialog
          incompleteSteps={incompleteSteps}
          onCancel={() => setSubmitDialog(false)}
          onConfirm={async () => { setSubmitDialog(false); await performSubmit(); }}
        />
      )}
    </Shell>
  );
}

// ── Submit confirmation dialog with incomplete-step list ─────────────────
function SubmitDialog({ incompleteSteps, onCancel, onConfirm }) {
  const hasIncomplete = incompleteSteps.length > 0;
  return (
    <Modal onCancel={onCancel}>
      <div style={{ fontSize: 18, fontWeight: 700, color: KA.navy, marginBottom: 10 }}>
        Submit the department review?
      </div>
      {hasIncomplete ? (
        <>
          <div style={{
            background: "#FBF1DC", borderLeft: `3px solid ${KA.gold}`,
            padding: "12px 14px", borderRadius: 6, marginBottom: 14,
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: KA.navy, marginBottom: 6 }}>
              These steps are not yet marked complete:
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: KA.ink, lineHeight: 1.5 }}>
              {incompleteSteps.map((s) => (<li key={s.id}>{s.title}</li>))}
            </ul>
          </div>
          <div style={{ fontSize: 13, color: KA.ink, lineHeight: 1.55, marginBottom: 16 }}>
            You can go back to mark them complete, or submit the review as it is.
          </div>
        </>
      ) : (
        <div style={{ fontSize: 13, color: KA.ink, lineHeight: 1.55, marginBottom: 16 }}>
          All six steps are marked complete. Submitting will close the
          review for this department.
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <button onClick={onCancel} style={ghostBtn}>
          {hasIncomplete ? "Go back" : "Cancel"}
        </button>
        <button onClick={onConfirm} style={{ ...primaryBtn, background: KA.navy }}>
          {hasIncomplete ? "Submit anyway" : "Submit"}
        </button>
      </div>
    </Modal>
  );
}

// ── Per-step completion checkbox + rules display ────────────────────────
//
// Sits at the bottom of every step body. Shows what's needed to complete
// the step, and offers a checkbox the HOD ticks once everything's captured.
// Ticking writes a completedAt timestamp to the segment doc; unticking
// clears it. Completion enables the green rail indicator and the
// "✓ Step complete" badge in the title bar.
//
// The HOD can freely uncheck a step they've already marked complete (e.g.
// they're going back to add more detail). Completion is a soft state, not
// a one-way gate.
function StepCompletionControl({ step, deptId, segment, onToggle }) {
  const { isComplete, canComplete, missing } = getStepCompletion(step, segment, deptId);

  return (
    <div style={{
      marginTop: 22,
      paddingTop: 16,
      borderTop: `1px solid ${KA.line}`,
    }}>
      {!isComplete && missing.length > 0 && (
        <div style={{
          background: KA.paper, border: `1px solid ${KA.line}`,
          borderRadius: 8, padding: "10px 12px", marginBottom: 12,
        }}>
          <div style={{
            fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
            color: KA.gold, marginBottom: 6,
          }}>
            TO MARK THIS STEP COMPLETE
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 3 }}>
            {missing.map((m, i) => (
              <li key={i} style={{ fontSize: 12.5, color: KA.ink, lineHeight: 1.5 }}>
                {m}
              </li>
            ))}
          </ul>
        </div>
      )}

      <label style={{
        display: "flex", alignItems: "center", gap: 10,
        cursor: (canComplete || isComplete) ? "pointer" : "not-allowed",
        opacity: (canComplete || isComplete) ? 1 : 0.55,
        userSelect: "none",
      }}>
        <input
          type="checkbox"
          checked={isComplete}
          disabled={!canComplete && !isComplete}
          onChange={(e) => onToggle(e.target.checked)}
          style={{
            width: 20, height: 20, accentColor: "#1B7A3D",
            cursor: "inherit",
          }}
        />
        <span style={{
          fontSize: 14, fontWeight: 700,
          color: isComplete ? "#1B7A3D" : KA.navy,
        }}>
          {isComplete ? "✓ Step marked complete" : "Mark this step complete"}
        </span>
      </label>
    </div>
  );
}

// ── HOD-facing thank-you (no AI mentioned, ever) ─────────────────────────
function ThankYou({ departmentName }) {
  return (
    <Shell>
      <div style={{
        ...card, textAlign: "center", padding: "56px 32px", marginTop: 40,
      }}>
        <div style={{
          fontSize: 48, color: KA.gold, fontFamily: "Georgia, serif",
          marginBottom: 12, lineHeight: 1,
        }}>
          ✓
        </div>
        <div style={{
          fontFamily: "Georgia, serif", fontSize: 24, fontWeight: 700,
          color: KA.navy, marginBottom: 10,
        }}>
          Your session is saved, thank you!
        </div>
        <div style={{ fontSize: 14, color: KA.mute, lineHeight: 1.6, maxWidth: 480, margin: "0 auto" }}>
          The {departmentName} department review has been submitted. You may
          close this tab.
        </div>
      </div>
    </Shell>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Compile session — deterministic, no AI
//
// Walks the six segment docs and produces a single structured blob that
// becomes the parent review's compiledSession field. This is the source of
// truth used by every later analysis (and re-analysis) — the LLM never
// sees the raw segments, only this compiled view.
// ═════════════════════════════════════════════════════════════════════════
function compileSession(deptId, deptName, segments) {
  const out = {
    departmentId: deptId,
    departmentName: deptName,
    compiledAt: new Date().toISOString(),
    steps: [],
  };

  for (const step of STEPS) {
    const seg = segments[step.id] || {};
    const stepBlock = {
      stepId: step.id,
      stepTitle: step.title,
      stepPrompt: step.prompt,
      kind: step.kind,
      typedNotes: "",
      audioTranscript: (typeof seg.transcript === "string" ? seg.transcript : "").trim(),
      capabilities: [],
    };

    if (step.kind === "capability") {
      const exs = CAPABILITY_EXAMPLES[deptId] || [];
      const notesObj = (seg.notes && typeof seg.notes === "object") ? seg.notes : {};
      const tempsObj = seg.temps || {};
      stepBlock.capabilities = exs.map((ex) => ({
        capabilityId: ex.id,
        title: ex.title,
        description: ex.body,
        roomFamiliarity: tempsObj[ex.id] || null,
        roomFamiliarityLabel:
          (TEMP_OPTIONS.find((o) => o.id === tempsObj[ex.id]) || {}).label || null,
        typedNotes: (notesObj[ex.id] || "").trim(),
      }));
    } else {
      stepBlock.typedNotes = (typeof seg.notes === "string" ? seg.notes : "").trim();
    }

    out.steps.push(stepBlock);
  }

  return out;
}

// ═════════════════════════════════════════════════════════════════════════
// Discussion / Capability step components
// ═════════════════════════════════════════════════════════════════════════
function DiscussionStep({ deptId, dept, step, segment, onUpdate }) {
  const mode = segment.mode || "type";
  return (
    <div>
      <ModeToggle mode={mode} onChange={(m) => onUpdate({ mode: m }, true)} />
      {mode === "type" ? (
        <TypePad value={segment.notes || ""} onChange={(v) => onUpdate({ notes: v })} />
      ) : (
        <RecordPad
          deptId={deptId} dept={dept} step={step}
          segment={segment} onUpdate={onUpdate}
        />
      )}
    </div>
  );
}

function CapabilityStep({ deptId, dept, step, segment, onUpdate }) {
  const examples = CAPABILITY_EXAMPLES[deptId] || [];
  const temps = segment.temps || {};
  const notes = segment.notes || {};

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {examples.map((ex, i) => (
        <div key={ex.id} style={{ border: `1px solid ${KA.line}`, borderRadius: 10, overflow: "hidden" }}>
          <div style={{ background: KA.paper, padding: "14px 16px", borderBottom: `1px solid ${KA.line}` }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <span style={{
                fontSize: 11, fontWeight: 700, color: "#fff", background: KA.navy,
                borderRadius: 4, padding: "2px 7px",
              }}>{i + 1}</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: KA.navy }}>{ex.title}</span>
            </div>
            <div style={{ fontSize: 13, color: KA.ink, lineHeight: 1.6, marginTop: 8 }}>{ex.body}</div>
            {ex.artifact && (
              <img src={ex.artifact} alt={ex.title}
                style={{ maxWidth: "100%", borderRadius: 6, marginTop: 10, border: `1px solid ${KA.line}` }} />
            )}
            <div style={{ fontSize: 11, color: KA.mute, fontStyle: "italic", marginTop: 8 }}>
              Read this to the room, or turn the screen to show it.
            </div>
          </div>
          <div style={{ padding: "12px 16px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: KA.gold, letterSpacing: "0.08em", marginBottom: 8 }}>
              ROOM FAMILIARITY
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {TEMP_OPTIONS.map((opt) => {
                const active = temps[ex.id] === opt.id;
                return (
                  <button
                    key={opt.id}
                    onClick={() => onUpdate({ temps: { ...temps, [ex.id]: opt.id } }, true)}
                    style={{
                      fontSize: 12, padding: "7px 11px", borderRadius: 999,
                      border: `1px solid ${active ? KA.navy : KA.line}`,
                      background: active ? KA.navy : "#fff",
                      color: active ? "#fff" : KA.ink,
                      cursor: "pointer", fontWeight: active ? 700 : 400,
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <div style={{ marginTop: 12 }}>
              <textarea
                value={notes[ex.id] || ""}
                onChange={(e) => onUpdate({ notes: { ...notes, [ex.id]: e.target.value } })}
                placeholder="Capture the discussion on this capability…"
                rows={3}
                style={textareaStyle}
              />
            </div>
          </div>
        </div>
      ))}

      <div style={{ borderTop: `1px dashed ${KA.line}`, paddingTop: 14 }}>
        <div style={{ fontSize: 12, color: KA.mute, marginBottom: 8 }}>
          Prefer to record the whole capability discussion instead of typing per card? Run it as one audio segment:
        </div>
        <RecordPad deptId={deptId} dept={dept} step={step} segment={segment} onUpdate={onUpdate} compact />
      </div>
    </div>
  );
}

function TypePad({ value, onChange }) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Type the department's notes here. Saves automatically as you go."
      rows={9}
      style={textareaStyle}
    />
  );
}

// ═════════════════════════════════════════════════════════════════════════
// RecordPad — audio capture only
//
// The pipeline is now JUST: upload → processAudioFile → store transcript.
// No AI summary at this stage. The final session-level analysis happens on
// submit. From the HOD's perspective this is identical to before — they
// see "Processed" when the transcript lands and that's the end of the loop.
// ═════════════════════════════════════════════════════════════════════════
function RecordPad({ deptId, dept, step, segment, onUpdate, compact }) {
  const [isRecording, setIsRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [localError, setLocalError] = useState("");

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const audioStatus = segment.audioStatus || "none";
  // none | uploading | processing | done | error

  useEffect(() => () => clearInterval(timerRef.current), []);

  const startRecording = async () => {
    setLocalError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = ["audio/mp4", "audio/webm;codecs=opus", "audio/webm", "audio/ogg"]
        .find((t) => MediaRecorder.isTypeSupported(t)) || "";
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType });
        const ext = mr.mimeType.includes("mp4") ? "m4a"
          : mr.mimeType.includes("ogg") ? "ogg" : "webm";
        const file = new File([blob], `${step.id}.${ext}`, { type: mr.mimeType });
        stream.getTracks().forEach((t) => t.stop());
        processInBackground(file);
      };
      mr.start(250);
      setIsRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((t) => t + 1), 1000);
    } catch {
      setLocalError("Microphone access denied. Allow microphone permissions and retry.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) mediaRecorderRef.current.stop();
    setIsRecording(false);
    clearInterval(timerRef.current);
  };

  const processInBackground = async (file) => {
    try {
      onUpdate({ audioStatus: "uploading" }, true);
      const auth = getAuth();
      const fbUser = auth.currentUser;
      if (!fbUser) throw new Error("Not signed in.");
      const idToken = await fbUser.getIdToken();

      const storage = getStorage();
      const ext = file.name.split(".").pop();
      const storagePath = `audio-uploads/${fbUser.uid}/${Date.now()}.${ext}`;
      await new Promise((resolve, reject) => {
        const task = uploadBytesResumable(ref(storage, storagePath), file);
        task.on("state_changed", null, reject, resolve);
      });

      onUpdate({ audioStatus: "processing" }, true);
      const tRes = await fetch(`${FUNCTIONS_BASE_URL}/processAudioFile`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ storagePath, language: "en" }),
      });
      const tData = await tRes.json();
      if (!tRes.ok) throw new Error(tData.error || "Transcription failed.");
      const transcriptText = (tData.transcript?.text || "").trim();
      if (!transcriptText) {
        throw new Error("No speech detected in the recording — please record again and speak clearly.");
      }
      onUpdate({ audioStatus: "done", transcript: transcriptText }, true);
    } catch (e) {
      console.error("Recording pipeline failed:", e);
      setLocalError(e.message);
      onUpdate({ audioStatus: "error" }, true);
    }
  };

  return (
    <div style={{
      border: `1px solid ${KA.line}`, borderRadius: 10,
      padding: compact ? "12px 14px" : "16px",
      background: compact ? KA.paper : "#fff",
    }}>
      {!isRecording && audioStatus === "none" && (
        <button onClick={startRecording} style={{ ...primaryBtn, background: KA.red }}>
          ● Start recording this segment
        </button>
      )}

      {isRecording && (
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <span style={{
            width: 12, height: 12, borderRadius: "50%", background: KA.red,
            animation: "drpulse 1s infinite", display: "inline-block",
          }} />
          <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 700, color: KA.ink }}>
            {Math.floor(elapsed / 60)}:{(elapsed % 60).toString().padStart(2, "0")}
          </span>
          <button onClick={stopRecording} style={{ ...ghostBtn, marginLeft: "auto" }}>
            ■ Stop & process
          </button>
        </div>
      )}

      {audioStatus !== "none" && !isRecording && (
        <AudioStatusBlock status={audioStatus} />
      )}

      {audioStatus !== "none" && audioStatus !== "uploading" && audioStatus !== "processing" && (
        <button onClick={startRecording} style={{ ...ghostBtn, marginTop: 10, fontSize: 12 }}>
          Record again (replaces this segment)
        </button>
      )}

      {localError && (
        <div style={{
          marginTop: 10, fontSize: 12, color: KA.red,
          background: "#FDECEC", borderRadius: 6, padding: "8px 10px",
        }}>
          {localError}
        </div>
      )}

      <div style={{ fontSize: 11, color: KA.mute, marginTop: 10, lineHeight: 1.5 }}>
        Recording uploads and processes in the background — move straight to
        the next step. Notes from typing and recording are combined when you
        complete the review.
      </div>
    </div>
  );
}

function AudioStatusBlock({ status }) {
  const map = {
    uploading:  { label: "Uploading audio…",  spin: true },
    processing: { label: "Transcribing…",     spin: true },
    done:       { label: "Processed",         spin: false },
    error:      { label: "Processing failed", spin: false },
  };
  const s = map[status] || map.error;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
      {s.spin && (
        <span style={{
          width: 12, height: 12, borderRadius: "50%",
          border: `2px solid ${KA.line}`, borderTopColor: KA.navy,
          animation: "drspin 0.8s linear infinite", display: "inline-block",
        }} />
      )}
      <span style={{
        fontWeight: 700,
        color: status === "error" ? KA.red : status === "done" ? KA.navy : KA.mute,
      }}>
        {s.label}
      </span>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// ADMIN HOME — dept tiles + roll-up + (superadmin) PII audit
// ═════════════════════════════════════════════════════════════════════════
function AdminHome({ hasDevTools, onOpenDepartment, onOpenRollup, onOpenDevTools }) {
  const [meta, setMeta] = useState({});  // deptId -> parent doc data

  useEffect(() => {
    (async () => {
      const out = {};
      for (const d of DEPARTMENTS) {
        try {
          const snap = await getDoc(doc(db, "departmentReviews", d.id));
          out[d.id] = snap.exists() ? snap.data() : null;
        } catch {
          out[d.id] = null;
        }
      }
      setMeta(out);
    })();
  }, []);

  return (
    <Shell>
      <Header
        title="Department Reviews — Admin"
        subtitle="Open a department to view its analysis, or aggregate across all five."
      />

      <div style={{ display: "grid", gap: 12 }}>
        {DEPARTMENTS.map((d) => {
          const m = meta[d.id];
          const badge = buildAdminBadge(m);
          return (
            <button
              key={d.id}
              onClick={() => onOpenDepartment(d.id)}
              style={{
                ...card, textAlign: "left", cursor: "pointer",
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}
            >
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: KA.navy }}>{d.name}</div>
                <div style={{ fontSize: 12, color: KA.mute, marginTop: 2 }}>{badge.label}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {badge.dot && (
                  <span style={{
                    width: 10, height: 10, borderRadius: "50%", background: badge.dot,
                  }} />
                )}
                <span style={{ color: KA.gold, fontSize: 18 }}>→</span>
              </div>
            </button>
          );
        })}
      </div>

      <button
        onClick={onOpenRollup}
        style={{ ...primaryBtn, marginTop: 20, width: "100%", background: KA.navy }}
      >
        Cross-department roll-up & at-a-glance
      </button>

      {hasDevTools && (
        <button
          onClick={onOpenDevTools}
          style={{
            marginTop: 10, width: "100%", padding: "11px 20px",
            background: "transparent", color: KA.mute,
            border: `1px dashed ${KA.line}`, borderRadius: 8,
            cursor: "pointer", fontSize: 13, fontFamily: "inherit",
          }}
        >
          🛠 Dev tools
        </button>
      )}
    </Shell>
  );
}

function buildAdminBadge(m) {
  if (!m) return { label: "Not yet started", dot: null };
  if (m.status !== "submitted") return { label: "In progress", dot: "#9CA3AF" };
  if (m.summaryStatus === "ready") return { label: "Submitted · summary ready", dot: "#22C55E" };
  if (m.summaryStatus === "failed") return { label: "Submitted · summary failed", dot: KA.red };
  return { label: "Submitted · summary pending", dot: "#C8973A" };
}

// ═════════════════════════════════════════════════════════════════════════
// ADMIN DEPARTMENT DETAIL — read summary, regenerate, export PDF
// ═════════════════════════════════════════════════════════════════════════
function AdminDepartmentDetail({ departmentId, onBack }) {
  const dept = DEPARTMENTS.find((d) => d.id === departmentId);
  const [parent, setParent] = useState(null);
  const [pdInput, setPdInput] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [viewAsHOD, setViewAsHOD] = useState(false);
  const [segments, setSegments] = useState(null);  // lazy-loaded for the HOD viewer
  const [exportingPdf, setExportingPdf] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const parentSnap = await getDoc(doc(db, "departmentReviews", departmentId));
      const parentData = parentSnap.exists() ? parentSnap.data() : null;
      setParent(parentData);

      // Fetch the pdInputs doc by its deterministic ID. The Cloud Function
      // writes to `pdInputs/dept-{departmentId}`, so a direct getDoc avoids
      // needing a composite Firestore index and is faster than a query.
      try {
        const pdSnap = await getDoc(doc(db, "pdInputs", `dept-${departmentId}`));
        setPdInput(pdSnap.exists() ? { id: pdSnap.id, ...pdSnap.data() } : null);
      } catch (e) {
        console.error("pdInput load failed:", e);
        setPdInput(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [departmentId]);

  const runRegenerate = async () => {
    setConfirmRegen(false);
    setRegenerating(true);
    try {
      // Mark pending so the home view's badge flips.
      await setDoc(
        doc(db, "departmentReviews", departmentId),
        { summaryStatus: "pending", updatedAt: serverTimestamp() },
        { merge: true }
      );

      const auth = getAuth();
      const fbUser = auth.currentUser;
      const idToken = fbUser ? await fbUser.getIdToken() : null;

      // Admin regenerations use a normal fetch (not sendBeacon) — admin is
      // willing to wait, and we want surfaced errors here. The function
      // will overwrite the existing pdInputs doc.
      const res = await fetch(`${FUNCTIONS_BASE_URL}/generateSessionSummary`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        },
        body: JSON.stringify({ departmentId }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      await load();
    } catch (e) {
      console.error("Regenerate failed:", e);
      alert("Regeneration failed: " + (e.message || "Unknown error"));
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return <Shell><div style={{ ...card, textAlign: "center", color: KA.mute }}>Loading…</div></Shell>;
  }

  return (
    <Shell>
      <Header
        title={`${dept.name} — Department Review`}
        subtitle={parent?.status === "submitted"
          ? `Submitted ${formatDate(parent.submittedAt)}`
          : "Not yet submitted"
        }
        right={<button onClick={onBack} style={ghostBtn}>← All departments</button>}
      />

      {parent?.status !== "submitted" ? (
        <div style={{ ...card, textAlign: "center", color: KA.mute, padding: 32 }}>
          This department hasn't submitted their review yet. Analysis becomes
          available after submission.
        </div>
      ) : (
        <>
          {/* Status + actions */}
          <div style={{ ...card, marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: KA.navy }}>
                  Analysis status: <SummaryStatusBadge status={parent.summaryStatus} />
                </div>
                {pdInput?.generatedAt && (
                  <div style={{ fontSize: 11, color: KA.mute, marginTop: 4 }}>
                    Last generated {formatDate(pdInput.generatedAt)}
                  </div>
                )}
                {parent.summaryStatus === "failed" && parent.summaryStatusMessage && (
                  <div style={{ fontSize: 12, color: KA.red, marginTop: 6 }}>
                    {parent.summaryStatusMessage}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button onClick={() => setViewAsHOD(true)} style={ghostBtn}>
                  👁 View as HOD
                </button>
                {pdInput?.summary && (
                  <button
                    onClick={async () => {
                      setExportingPdf(true);
                      try {
                        await exportDepartmentPDF(dept, parent, pdInput);
                      } catch (e) {
                        console.error("PDF export failed:", e);
                        alert("PDF export failed: " + (e.message || "Unknown error"));
                      } finally {
                        setExportingPdf(false);
                      }
                    }}
                    disabled={exportingPdf}
                    style={{ ...ghostBtn, opacity: exportingPdf ? 0.5 : 1 }}
                  >
                    {exportingPdf ? "Generating PDF…" : "📄 Export PDF"}
                  </button>
                )}
                <button
                  onClick={() => setConfirmRegen(true)}
                  disabled={regenerating}
                  style={{ ...primaryBtn, opacity: regenerating ? 0.5 : 1 }}
                >
                  {regenerating ? "Regenerating…" : "Regenerate"}
                </button>
              </div>
            </div>
          </div>

          {/* Summary view */}
          {pdInput?.summary ? (
            <DepartmentSummaryView summary={pdInput.summary} />
          ) : (
            <div style={{ ...card, textAlign: "center", color: KA.mute, padding: 32 }}>
              {parent.summaryStatus === "pending"
                ? "Analysis is queued — refresh in a moment."
                : "No analysis available. Use Regenerate to produce one."}
            </div>
          )}
        </>
      )}

      {confirmRegen && (
        <Modal onCancel={() => setConfirmRegen(false)}>
          <div style={{ fontSize: 17, fontWeight: 700, color: KA.navy, marginBottom: 10 }}>
            Regenerate the analysis?
          </div>
          <div style={{ fontSize: 13, color: KA.ink, lineHeight: 1.6, marginBottom: 16 }}>
            This will overwrite the existing analysis with a fresh one. The
            compiled session data is unchanged.
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button onClick={() => setConfirmRegen(false)} style={ghostBtn}>Cancel</button>
            <button onClick={runRegenerate} style={primaryBtn}>Regenerate</button>
          </div>
        </Modal>
      )}

      {viewAsHOD && (
        <HODReadOnlyViewer
          departmentId={departmentId}
          dept={dept}
          onClose={() => setViewAsHOD(false)}
        />
      )}
    </Shell>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// HOD READ-ONLY VIEWER — admin sees the HOD's session exactly as captured
//
// Renders the six steps' captured content (typed notes, audio transcripts,
// capability taps, per-step completion status) in a layout that mirrors
// the HOD's session screen but with everything locked. No editing, no
// recording, no marking complete — just visibility.
//
// Loads the segments collection fresh on mount so the admin always sees
// the current state, not a cached version.
// ═════════════════════════════════════════════════════════════════════════
function HODReadOnlyViewer({ departmentId, dept, onClose }) {
  const [segments, setSegments] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "departmentReviews", departmentId, "segments"));
        const data = {};
        snap.forEach((d) => { data[d.id] = d.data(); });
        setSegments(data);
      } catch (e) {
        console.error("HOD viewer load failed:", e);
        setSegments({});
      }
    })();
  }, [departmentId]);

  if (!segments) {
    return (
      <Modal onCancel={onClose} wide>
        <div style={{ textAlign: "center", color: KA.mute, padding: 24 }}>Loading…</div>
      </Modal>
    );
  }

  const step = STEPS[stepIndex];
  const seg = segments[step.id] || {};
  const completion = getStepCompletion(step, seg, departmentId);

  return (
    <Modal onCancel={onClose} wide>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: KA.gold }}>
            VIEWING AS HOD · READ-ONLY
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: KA.navy, fontFamily: "Georgia, serif", marginTop: 2 }}>
            {dept.name} — Step {stepIndex + 1} of {STEPS.length}
          </div>
        </div>
        <button onClick={onClose} style={ghostBtn}>✕ Close</button>
      </div>

      {/* Rail */}
      <div style={{ marginBottom: 16 }}>
        <StepRail
          steps={STEPS}
          current={stepIndex}
          segments={segments}
          onJump={setStepIndex}
        />
      </div>

      <div style={{ background: KA.paper, border: `1px solid ${KA.line}`, borderRadius: 10, padding: 18, maxHeight: "55vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 12, flexWrap: "wrap" }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", color: KA.gold, textTransform: "uppercase" }}>
            {step.title}
          </div>
          {completion.isComplete && (
            <span style={{
              fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 999,
              background: "#E7F3EC", color: "#1B7A3D",
            }}>
              ✓ Step complete
            </span>
          )}
        </div>
        <div style={{ fontSize: 14, color: KA.ink, lineHeight: 1.6, marginBottom: 18 }}>
          {step.prompt}
        </div>

        {step.kind === "discussion" ? (
          <ReadOnlyDiscussion seg={seg} />
        ) : (
          <ReadOnlyCapability seg={seg} departmentId={departmentId} />
        )}

        {!completion.isComplete && completion.missing.length > 0 && (
          <div style={{
            marginTop: 14, padding: "10px 12px",
            background: "#FBF1DC", borderLeft: `3px solid ${KA.gold}`,
            borderRadius: 6,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: KA.navy, marginBottom: 4 }}>
              Not yet marked complete:
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: KA.ink, lineHeight: 1.5 }}>
              {completion.missing.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 14, gap: 10 }}>
        <button
          onClick={() => stepIndex > 0 && setStepIndex(stepIndex - 1)}
          disabled={stepIndex === 0}
          style={{ ...ghostBtn, opacity: stepIndex === 0 ? 0.4 : 1 }}
        >
          ← Previous
        </button>
        <button
          onClick={() => stepIndex < STEPS.length - 1 && setStepIndex(stepIndex + 1)}
          disabled={stepIndex === STEPS.length - 1}
          style={{ ...primaryBtn, background: KA.navy, opacity: stepIndex === STEPS.length - 1 ? 0.4 : 1 }}
        >
          Next step →
        </button>
      </div>
    </Modal>
  );
}

function ReadOnlyDiscussion({ seg }) {
  const typedNotes = (typeof seg.notes === "string" ? seg.notes : "").trim();
  const transcript = (typeof seg.transcript === "string" ? seg.transcript : "").trim();
  const audioStatus = seg.audioStatus;

  if (!typedNotes && !transcript) {
    return <div style={{ fontSize: 13, color: KA.mute, fontStyle: "italic" }}>(no captured content)</div>;
  }

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {typedNotes && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: KA.gold, marginBottom: 6, letterSpacing: "0.08em" }}>
            TYPED NOTES
          </div>
          <div style={{
            background: "#fff", border: `1px solid ${KA.line}`, borderRadius: 8,
            padding: "10px 12px", fontSize: 13.5, lineHeight: 1.6, color: KA.ink,
            whiteSpace: "pre-wrap",
          }}>
            {typedNotes}
          </div>
        </div>
      )}
      {transcript && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: KA.gold, marginBottom: 6, letterSpacing: "0.08em" }}>
            AUDIO TRANSCRIPT
            {audioStatus && audioStatus !== "done" && (
              <span style={{ color: KA.mute, marginLeft: 6, fontWeight: 400 }}>
                ({audioStatus})
              </span>
            )}
          </div>
          <div style={{
            background: "#fff", border: `1px solid ${KA.line}`, borderRadius: 8,
            padding: "10px 12px", fontSize: 13.5, lineHeight: 1.6, color: KA.ink,
            whiteSpace: "pre-wrap",
          }}>
            {transcript}
          </div>
        </div>
      )}
    </div>
  );
}

function ReadOnlyCapability({ seg, departmentId }) {
  const examples = CAPABILITY_EXAMPLES[departmentId] || [];
  const temps = seg.temps || {};
  const notes = (seg.notes && typeof seg.notes === "object") ? seg.notes : {};
  const transcript = (typeof seg.transcript === "string" ? seg.transcript : "").trim();

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {examples.map((ex, i) => {
        const tempId = temps[ex.id];
        const tempLabel = (TEMP_OPTIONS.find((t) => t.id === tempId) || {}).label;
        const cardNotes = (notes[ex.id] || "").trim();
        return (
          <div key={ex.id} style={{
            background: "#fff", border: `1px solid ${KA.line}`, borderRadius: 8,
            padding: "12px 14px",
          }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: KA.navy }}>
              {i + 1}. {ex.title}
            </div>
            <div style={{ fontSize: 12, color: KA.mute, lineHeight: 1.55, marginTop: 4 }}>
              {ex.body}
            </div>
            <div style={{ marginTop: 8, display: "flex", gap: 12, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, color: KA.gold, fontWeight: 700 }}>
                Room familiarity:{" "}
                <span style={{ color: tempLabel ? KA.ink : KA.red, fontWeight: 600 }}>
                  {tempLabel || "(not tapped)"}
                </span>
              </span>
            </div>
            {cardNotes && (
              <div style={{
                marginTop: 8, fontSize: 13, color: KA.ink, lineHeight: 1.55,
                background: KA.paper, borderRadius: 6, padding: "8px 10px",
                whiteSpace: "pre-wrap",
              }}>
                {cardNotes}
              </div>
            )}
          </div>
        );
      })}
      {transcript && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: KA.gold, marginBottom: 6, letterSpacing: "0.08em" }}>
            COMBINED AUDIO TRANSCRIPT
          </div>
          <div style={{
            background: "#fff", border: `1px solid ${KA.line}`, borderRadius: 8,
            padding: "10px 12px", fontSize: 13.5, lineHeight: 1.6, color: KA.ink,
            whiteSpace: "pre-wrap",
          }}>
            {transcript}
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryStatusBadge({ status }) {
  const style = {
    ready:   { bg: "#E7F3EC", fg: "#1B7A3D", label: "Ready" },
    pending: { bg: "#FBF1DC", fg: "#9A7B26", label: "Pending" },
    failed:  { bg: "#FDECEC", fg: KA.red,    label: "Failed" },
  }[status] || { bg: KA.paper, fg: KA.mute, label: status || "—" };
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 999,
      background: style.bg, color: style.fg, marginLeft: 6,
    }}>
      {style.label}
    </span>
  );
}

// ── Render a single department's structured summary ──────────────────────
const BLOCKER_LABELS = {
  skill:         "Skill gap",
  access:        "Tool access",
  policy:        "Policy uncertainty",
  philosophical: "Philosophical objection",
  time:          "Time / bandwidth",
};
const CONFIDENCE_STYLE = {
  confident: { bg: "#E7F3EC", fg: "#1B7A3D", label: "Confident" },
  mixed:     { bg: "#FBF1DC", fg: "#9A7B26", label: "Mixed" },
  uncertain: { bg: "#FDECEC", fg: KA.red,    label: "Uncertain" },
};

function DepartmentSummaryView({ summary }) {
  const conf = CONFIDENCE_STYLE[summary.confidence] || CONFIDENCE_STYLE.mixed;
  return (
    <div style={{ display: "grid", gap: 16 }}>
      {summary.overview && (
        <div style={{ ...card }}>
          <SectionTitle>Overview</SectionTitle>
          <div style={{ fontSize: 14, color: KA.ink, lineHeight: 1.65 }}>
            {summary.overview}
          </div>
          <div style={{ marginTop: 10 }}>
            <span style={{
              fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 999,
              background: conf.bg, color: conf.fg,
            }}>
              Department confidence: {conf.label}
            </span>
          </div>
        </div>
      )}

      {summary.themes?.length > 0 && (
        <div style={{ ...card }}>
          <SectionTitle>Themes across the session</SectionTitle>
          <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 6 }}>
            {summary.themes.map((t, i) => (
              <li key={i} style={{ fontSize: 13.5, color: KA.ink, lineHeight: 1.6 }}>
                <strong>{t.theme}</strong>{t.detail ? ` — ${t.detail}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}

      {summary.capability_gaps?.length > 0 && (
        <div style={{ ...card }}>
          <SectionTitle>Capabilities they didn't know about</SectionTitle>
          <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 5 }}>
            {summary.capability_gaps.map((g, i) => (
              <li key={i} style={{ fontSize: 13, color: KA.ink, lineHeight: 1.55 }}>
                <strong>{g.capability}</strong> — {g.description}
              </li>
            ))}
          </ul>
        </div>
      )}

      {summary.blockers?.length > 0 && (
        <div style={{ ...card }}>
          <SectionTitle>Blockers</SectionTitle>
          <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 5 }}>
            {summary.blockers.map((b, i) => (
              <li key={i} style={{ fontSize: 13, color: KA.ink, lineHeight: 1.55 }}>
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 4,
                  background: KA.paper, border: `1px solid ${KA.line}`, marginRight: 6,
                }}>
                  {BLOCKER_LABELS[b.type] || b.type}
                </span>
                {b.description}
              </li>
            ))}
          </ul>
        </div>
      )}

      {summary.pd_signals?.length > 0 && (
        <div style={{ ...card }}>
          <SectionTitle>PD signals</SectionTitle>
          <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 5 }}>
            {summary.pd_signals.map((s, i) => (
              <li key={i} style={{ fontSize: 13, color: KA.ink, lineHeight: 1.55 }}>{s}</li>
            ))}
          </ul>
        </div>
      )}

      {summary.recommended_next_steps?.length > 0 && (
        <div style={{ ...card }}>
          <SectionTitle>Recommended next steps</SectionTitle>
          <ol style={{ margin: 0, paddingLeft: 20, display: "grid", gap: 5 }}>
            {summary.recommended_next_steps.map((s, i) => (
              <li key={i} style={{ fontSize: 13, color: KA.ink, lineHeight: 1.55 }}>{s}</li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// ROLLUP — cross-department synthesis + chat
// ═════════════════════════════════════════════════════════════════════════
const ANTHROPIC_PROXY_URL = `${FUNCTIONS_BASE_URL}/anthropicProxy`;

async function callAnthropicProxy(messages, system) {
  const auth = getAuth();
  const fbUser = auth.currentUser;
  if (!fbUser) throw new Error("Not signed in.");
  const idToken = await fbUser.getIdToken();
  const res = await fetch(ANTHROPIC_PROXY_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
    body: JSON.stringify({
      model: "claude-sonnet-4-6",
      max_tokens: 2000,
      system, messages,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "AI request failed.");
  return (data.content || [])
    .filter((b) => b.type === "text")
    .map((b) => b.text)
    .join("")
    .trim();
}

const ROLLUP_SYSTEM =
  "You are a professional-development planning assistant for King's Academy " +
  "in Madaba, Jordan. You are working from per-department review analyses. " +
  "All identifying details were removed before this data reached you. Your " +
  "job is to help administrators plan effective PD. Be concrete, practical, " +
  "and honest — including about which needs are training problems and which " +
  "are policy or philosophical disagreements that PD cannot fix. Stay focused " +
  "on this department-review data; do not drift into unrelated topics.";

function buildSynthesisPrompt(aggregateText) {
  return `Here are the analyses from each department's review session:

${aggregateText}

Analyze across departments and return ONLY a JSON object — no preamble, no markdown fences — with exactly this shape:

{
  "themes": [
    {
      "theme": "Short name of a pattern that appears across two or more departments",
      "departments": ["Department names sharing this theme"],
      "description": "2-3 sentences explaining the theme and what's driving it.",
      "kind": "training" | "policy" | "mixed"
    }
  ],
  "where_to_start": [
    {
      "step": "What to do",
      "rationale": "Why this is the right place to start — leverage, breadth, low controversy, etc.",
      "departments": ["Who this serves"]
    }
  ],
  "groupings": [
    {
      "departments": ["Department names to handle together — always an array, even if just one"],
      "need": "What this group specifically needs",
      "format": "Suggested format — e.g. one combined hands-on workshop, a facilitated policy conversation, etc."
    }
  ]
}

Guidance:
- A "theme" must genuinely span 2+ departments. Order themes by how many departments share them.
- "kind": "training" = a skills workshop would address it; "policy" = it needs a school decision or a facilitated conversation, not a workshop; "mixed" = both.
- In "where_to_start", sequence by leverage: a training-kind theme shared by many departments is high-leverage and low-controversy — start there. Do NOT sequence a policy/philosophical split as if it were training.
- In "groupings", cluster departments with the same need so one session can serve several; keep a department separate when its need is distinct.
- Be honest. If something is a philosophical disagreement, say so plainly rather than dressing it up as a training need.`;
}

function Rollup({ onBack }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]); // [{ dept, pdInput }]

  const [synthLoading, setSynthLoading] = useState(false);
  const [synthError, setSynthError] = useState("");
  const [report, setReport] = useState(null);
  // Timestamp of the stored synthesis (when it was last generated/regenerated).
  // Null when no synthesis has been generated yet, or when a fresh in-memory
  // one hasn't been persisted.
  const [reportGeneratedAt, setReportGeneratedAt] = useState(null);
  const [confirmRegen, setConfirmRegen] = useState(false);

  const [chatHistory, setChatHistory] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  // null when no export in progress; "full" or "anon" while one is running.
  const [exportingPdf, setExportingPdf] = useState(null);

  useEffect(() => {
    (async () => {
      const out = [];
      for (const d of DEPARTMENTS) {
        try {
          // Same deterministic-ID fetch as AdminDepartmentDetail — no index needed.
          const pdSnap = await getDoc(doc(db, "pdInputs", `dept-${d.id}`));
          out.push({ dept: d, pdInput: pdSnap.exists() ? pdSnap.data() : null });
        } catch (e) {
          console.error(`Rollup load for ${d.id} failed:`, e);
          out.push({ dept: d, pdInput: null });
        }
      }
      setData(out);

      // Hydrate any stored synthesis the last time someone ran it.
      try {
        const rSnap = await getDoc(doc(db, "pdRollups", "current"));
        if (rSnap.exists()) {
          const r = rSnap.data();
          if (r.report) {
            setReport(r.report);
            setReportGeneratedAt(r.generatedAt || null);
          }
        }
      } catch (e) {
        console.error("Rollup hydration failed:", e);
      }

      setLoading(false);
    })();
  }, []);

  // Build the per-department analysis text the LLM aggregates over.
  const buildAggregateText = () => {
    const lines = [];
    data.forEach(({ dept, pdInput }) => {
      const s = pdInput?.summary;
      if (!s) return;
      lines.push(`=== ${dept.name} ===`);
      if (s.overview) lines.push(`Overview: ${s.overview}`);
      if (s.confidence) lines.push(`Confidence: ${s.confidence}`);
      if (s.themes?.length) {
        lines.push("Themes:");
        s.themes.forEach((t) => lines.push(`  - ${t.theme}${t.detail ? ": " + t.detail : ""}`));
      }
      if (s.capability_gaps?.length) {
        lines.push("Capability gaps:");
        s.capability_gaps.forEach((g) => lines.push(`  - ${g.capability}: ${g.description}`));
      }
      if (s.blockers?.length) {
        lines.push("Blockers:");
        s.blockers.forEach((b) => lines.push(`  - [${b.type}] ${b.description}`));
      }
      if (s.pd_signals?.length) {
        lines.push("PD signals:");
        s.pd_signals.forEach((p) => lines.push(`  - ${p}`));
      }
      lines.push("");
    });
    return lines.join("\n");
  };

  const hasData = data.some(({ pdInput }) => pdInput?.summary);

  const runSynthesis = async () => {
    setSynthLoading(true);
    setSynthError("");
    try {
      const agg = buildAggregateText();
      const prompt = buildSynthesisPrompt(agg);
      const raw = await callAnthropicProxy([{ role: "user", content: prompt }], ROLLUP_SYSTEM);
      const clean = raw.replace(/```json/gi, "").replace(/```/g, "").trim();
      const parsed = JSON.parse(clean);
      const nextReport = {
        themes: Array.isArray(parsed.themes) ? parsed.themes : [],
        where_to_start: Array.isArray(parsed.where_to_start) ? parsed.where_to_start : [],
        groupings: Array.isArray(parsed.groupings) ? parsed.groupings : [],
      };
      setReport(nextReport);
      setChatHistory([
        { role: "user", content: prompt },
        { role: "assistant", content: raw },
      ]);

      // Persist so the synthesis survives navigation away and is visible
      // to the next admin who opens this view. Stale-detection is left to
      // the admin — if a department's analysis is regenerated, this stored
      // synthesis is now from an older snapshot, but we trust the admin to
      // hit Regenerate when they want a refresh.
      try {
        await setDoc(doc(db, "pdRollups", "current"), {
          report: nextReport,
          generatedAt: serverTimestamp(),
        }, { merge: true });
        setReportGeneratedAt(new Date());
      } catch (e) {
        console.error("Rollup persistence failed:", e);
      }
    } catch (e) {
      console.error("Synthesis failed:", e);
      setSynthError("Could not synthesize themes. " + (e.message || "Please try again."));
    } finally {
      setSynthLoading(false);
    }
  };

  const sendChat = async () => {
    const q = chatInput.trim();
    if (!q || chatLoading) return;
    setChatInput("");
    const next = [...chatHistory, { role: "user", content: q }];
    setChatHistory(next);
    setChatLoading(true);
    try {
      const reply = await callAnthropicProxy(next, ROLLUP_SYSTEM);
      setChatHistory([...next, { role: "assistant", content: reply }]);
    } catch (e) {
      console.error("Chat failed:", e);
      setChatHistory([...next, { role: "assistant", content: "(Sorry — that request failed. Please try again.)" }]);
    } finally {
      setChatLoading(false);
    }
  };

  const visibleChat = chatHistory.slice(2);

  return (
    <Shell>
      <Header
        title="Cross-Department Roll-Up"
        subtitle="Aggregated analysis from every submitted department review"
        right={<button onClick={onBack} style={ghostBtn}>← Admin home</button>}
      />

      {loading ? (
        <div style={{ ...card, textAlign: "center", color: KA.mute }}>Loading…</div>
      ) : !hasData ? (
        <div style={{ ...card, textAlign: "center", color: KA.mute }}>
          No department analyses available yet. Roll-up populates as
          departments submit their reviews.
        </div>
      ) : (
        <>
          {!report ? (
            <div style={{ ...card, textAlign: "center" }}>
              <div style={{ fontSize: 13, color: KA.ink, lineHeight: 1.6, marginBottom: 14 }}>
                The aggregate below lists every department's analysis. Synthesize
                across them for cross-cutting themes and a recommended starting plan.
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
                <button
                  onClick={async () => {
                    setExportingPdf("overviews");
                    try { await exportPerDepartmentSummariesPDF(data); }
                    catch (e) { console.error("PDF export failed:", e); alert("PDF export failed: " + (e.message || "Unknown")); }
                    finally { setExportingPdf(null); }
                  }}
                  disabled={!!exportingPdf}
                  style={{ ...ghostBtn, opacity: exportingPdf ? 0.5 : 1 }}
                >
                  {exportingPdf === "overviews" ? "Generating PDF…" : "📄 Export overviews"}
                </button>
                <button
                  onClick={async () => {
                    setExportingPdf("zip");
                    try {
                      const { included, skipped } = await exportAllDepartmentPDFsAsZip(data);
                      if (skipped > 0) {
                        alert(`Zip downloaded with ${included} department PDF${included === 1 ? "" : "s"}. ${skipped} department${skipped === 1 ? " was" : "s were"} skipped (no analysis available).`);
                      }
                    } catch (e) {
                      console.error("Zip export failed:", e);
                      alert("Zip export failed: " + (e.message || "Unknown"));
                    } finally {
                      setExportingPdf(null);
                    }
                  }}
                  disabled={!!exportingPdf}
                  style={{ ...ghostBtn, opacity: exportingPdf ? 0.5 : 1 }}
                >
                  {exportingPdf === "zip" ? "Building zip…" : "🗂 Download all dept PDFs"}
                </button>
                <button
                  onClick={runSynthesis}
                  disabled={synthLoading}
                  style={{ ...primaryBtn, background: KA.navy, opacity: synthLoading ? 0.6 : 1 }}
                >
                  {synthLoading ? "Synthesizing…" : "✦ Synthesize themes"}
                </button>
              </div>
              {synthError && (
                <div style={{
                  marginTop: 12, fontSize: 12, color: KA.red,
                  background: "#FDECEC", borderRadius: 6, padding: "8px 10px",
                }}>
                  {synthError}
                </div>
              )}
            </div>
          ) : (
            <>
              <div style={{
                display: "flex", justifyContent: "space-between",
                alignItems: "center", marginBottom: 10, gap: 10, flexWrap: "wrap",
              }}>
                {reportGeneratedAt ? (
                  <div style={{ fontSize: 11.5, color: KA.mute }}>
                    Synthesis generated {formatDate(reportGeneratedAt)}
                  </div>
                ) : <span />}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    onClick={async () => {
                      setExportingPdf("overviews");
                      try { await exportPerDepartmentSummariesPDF(data); }
                      catch (e) { console.error("PDF export failed:", e); alert("PDF export failed: " + (e.message || "Unknown")); }
                      finally { setExportingPdf(null); }
                    }}
                    disabled={!!exportingPdf}
                    style={{ ...ghostBtn, opacity: exportingPdf ? 0.5 : 1 }}
                  >
                    {exportingPdf === "overviews" ? "Generating PDF…" : "📄 Export overviews"}
                  </button>
                  <button
                    onClick={async () => {
                      setExportingPdf("full");
                      try { await exportRollupPDF(report, data); }
                      catch (e) { console.error("PDF export failed:", e); alert("PDF export failed: " + (e.message || "Unknown")); }
                      finally { setExportingPdf(null); }
                    }}
                    disabled={!!exportingPdf}
                    style={{ ...ghostBtn, opacity: exportingPdf ? 0.5 : 1 }}
                  >
                    {exportingPdf === "full" ? "Generating PDF…" : "📄 Export full PDF"}
                  </button>
                  <button
                    onClick={async () => {
                      setExportingPdf("anon");
                      try { await exportAnonymizedRollupPDF(report); }
                      catch (e) { console.error("PDF export failed:", e); alert("PDF export failed: " + (e.message || "Unknown")); }
                      finally { setExportingPdf(null); }
                    }}
                    disabled={!!exportingPdf}
                    style={{ ...ghostBtn, opacity: exportingPdf ? 0.5 : 1 }}
                  >
                    {exportingPdf === "anon" ? "Generating PDF…" : "📄 Export anonymized PDF"}
                  </button>
                  <button
                    onClick={async () => {
                      setExportingPdf("zip");
                      try {
                        const { included, skipped } = await exportAllDepartmentPDFsAsZip(data);
                        if (skipped > 0) {
                          alert(`Zip downloaded with ${included} department PDF${included === 1 ? "" : "s"}. ${skipped} department${skipped === 1 ? " was" : "s were"} skipped (no analysis available).`);
                        }
                      } catch (e) {
                        console.error("Zip export failed:", e);
                        alert("Zip export failed: " + (e.message || "Unknown"));
                      } finally {
                        setExportingPdf(null);
                      }
                    }}
                    disabled={!!exportingPdf}
                    style={{ ...ghostBtn, opacity: exportingPdf ? 0.5 : 1 }}
                  >
                    {exportingPdf === "zip" ? "Building zip…" : "🗂 Download all dept PDFs"}
                  </button>
                  <button
                    onClick={() => setConfirmRegen(true)}
                    disabled={synthLoading}
                    style={{ ...primaryBtn, opacity: synthLoading ? 0.5 : 1 }}
                  >
                    {synthLoading ? "Regenerating…" : "Regenerate"}
                  </button>
                </div>
              </div>
              {synthError && (
                <div style={{
                  marginBottom: 12, fontSize: 12, color: KA.red,
                  background: "#FDECEC", borderRadius: 6, padding: "8px 10px",
                }}>
                  {synthError}
                </div>
              )}
              <SynthesisReport report={report} />
            </>
          )}

          {confirmRegen && (
            <Modal onCancel={() => setConfirmRegen(false)}>
              <div style={{ fontSize: 17, fontWeight: 700, color: KA.navy, marginBottom: 10 }}>
                Regenerate the synthesis?
              </div>
              <div style={{ fontSize: 13, color: KA.ink, lineHeight: 1.6, marginBottom: 16 }}>
                This will overwrite the stored synthesis with a fresh one,
                based on the current department analyses.
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button onClick={() => setConfirmRegen(false)} style={ghostBtn}>Cancel</button>
                <button
                  onClick={async () => { setConfirmRegen(false); await runSynthesis(); }}
                  style={primaryBtn}
                >
                  Regenerate
                </button>
              </div>
            </Modal>
          )}

          {report && (
            <div style={{ ...card, marginTop: 16 }}>
              <SectionTitle>Ask about these findings</SectionTitle>
              <div style={{ fontSize: 12, color: KA.mute, marginBottom: 12, lineHeight: 1.5 }}>
                Follow up on the synthesis — e.g. "which themes could one workshop
                cover?" or "draft a note to the English department about the
                stance issue."
              </div>
              {visibleChat.length > 0 && (
                <div style={{ display: "grid", gap: 10, marginBottom: 12 }}>
                  {visibleChat.map((m, i) => (
                    <div key={i} style={{
                      justifySelf: m.role === "user" ? "end" : "start",
                      maxWidth: "85%",
                      background: m.role === "user" ? KA.navy : KA.paper,
                      color: m.role === "user" ? "#fff" : KA.ink,
                      border: m.role === "user" ? "none" : `1px solid ${KA.line}`,
                      borderRadius: 10, padding: "10px 13px", fontSize: 13,
                      lineHeight: 1.55, whiteSpace: "pre-wrap",
                    }}>
                      {m.content}
                    </div>
                  ))}
                </div>
              )}
              {chatLoading && (
                <div style={{ fontSize: 12, color: KA.mute, marginBottom: 10 }}>Thinking…</div>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") sendChat(); }}
                  placeholder="Ask a follow-up question…"
                  style={{
                    flex: 1, boxSizing: "border-box",
                    border: `1px solid ${KA.line}`, borderRadius: 8,
                    padding: "10px 12px", fontSize: 13, fontFamily: "inherit", color: KA.ink,
                  }}
                />
                <button
                  onClick={sendChat}
                  disabled={chatLoading || !chatInput.trim()}
                  style={{ ...primaryBtn, opacity: chatLoading || !chatInput.trim() ? 0.5 : 1 }}
                >
                  Send
                </button>
              </div>
            </div>
          )}

          {/* Raw aggregate — source of truth */}
          <div style={{ ...card, marginTop: 16 }}>
            <SectionTitle>By department</SectionTitle>
            {data.map(({ dept, pdInput }) => {
              const s = pdInput?.summary;
              return (
                <div key={dept.id} style={{ marginBottom: 14, borderLeft: `3px solid ${KA.gold}`, paddingLeft: 12 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: KA.navy }}>{dept.name}</div>
                  {!s ? (
                    <div style={{ fontSize: 12, color: KA.mute, marginTop: 3 }}>No analysis yet</div>
                  ) : (
                    <div style={{ fontSize: 12.5, color: KA.ink, lineHeight: 1.55, marginTop: 3 }}>
                      {s.overview || "(no overview generated)"}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </Shell>
  );
}

function formatGroup(value) {
  if (Array.isArray(value)) return value.join(" · ");
  if (typeof value !== "string") return "";
  let s = value
    .replace(/([a-z])([A-Z])/g, "$1 · $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 · $2");
  s = s.replace(/\s*,\s*/g, " · ");
  return s;
}

const THEME_KIND_STYLE = {
  training: { bg: "#E7F3EC", fg: "#1B7A3D", label: "Training" },
  policy:   { bg: "#FDECEC", fg: KA.red,    label: "Policy / conversation" },
  mixed:    { bg: "#FBF1DC", fg: "#9A7B26", label: "Mixed" },
};

function SynthesisReport({ report }) {
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ ...card }}>
        <SectionTitle>Cross-cutting themes</SectionTitle>
        {report.themes.length === 0 ? (
          <div style={{ fontSize: 13, color: KA.mute }}>No shared themes identified.</div>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            {report.themes.map((t, i) => {
              const ks = THEME_KIND_STYLE[t.kind] || THEME_KIND_STYLE.mixed;
              return (
                <div key={i} style={{ borderLeft: `3px solid ${KA.gold}`, paddingLeft: 12 }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: KA.navy }}>{t.theme}</span>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 999,
                      background: ks.bg, color: ks.fg,
                    }}>{ks.label}</span>
                  </div>
                  <div style={{ fontSize: 11, color: KA.mute, margin: "3px 0 5px" }}>
                    {(t.departments || []).join(" · ")}
                  </div>
                  <div style={{ fontSize: 13, color: KA.ink, lineHeight: 1.55 }}>{t.description}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ ...card }}>
        <SectionTitle>Where to start</SectionTitle>
        <div style={{ display: "grid", gap: 10 }}>
          {report.where_to_start.map((s, i) => (
            <div key={i} style={{ display: "flex", gap: 12 }}>
              <div style={{
                flexShrink: 0, width: 24, height: 24, borderRadius: "50%",
                background: KA.red, color: "#fff", fontSize: 12, fontWeight: 700,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>{i + 1}</div>
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: KA.navy }}>{s.step}</div>
                <div style={{ fontSize: 12.5, color: KA.ink, lineHeight: 1.5, marginTop: 2 }}>{s.rationale}</div>
                {s.departments?.length > 0 && (
                  <div style={{ fontSize: 11, color: KA.mute, marginTop: 3 }}>
                    Serves: {s.departments.join(", ")}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ ...card }}>
        <SectionTitle>Who needs what</SectionTitle>
        <div style={{ display: "grid", gap: 10 }}>
          {report.groupings.map((g, i) => (
            <div key={i} style={{
              background: KA.paper, border: `1px solid ${KA.line}`,
              borderRadius: 8, padding: "10px 12px",
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: KA.navy }}>
                {formatGroup(g.departments ?? g.group)}
              </div>
              <div style={{ fontSize: 12.5, color: KA.ink, lineHeight: 1.5, marginTop: 3 }}>{g.need}</div>
              <div style={{ fontSize: 11.5, color: KA.gold, fontWeight: 700, marginTop: 4 }}>{g.format}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// PDF EXPORTS — direct jsPDF text rendering
//
// We build PDFs imperatively using jsPDF's doc.text/doc.setFont/doc.rect
// primitives rather than rendering HTML. Reasons:
//   1. doc.html() was producing blank pages (html2canvas captures the
//      offscreen rendering inconsistently).
//   2. Direct rendering produces TRUE text-selectable PDFs — readers can
//      copy/paste theme names, search within the document, etc.
//   3. No html2canvas dependency. jsPDF alone is enough.
//   4. The output is stable: jsPDF text rendering doesn't drift between
//      browser versions the way html2canvas does.
//
// The visual style is plainer than the HTML version (no rounded badges,
// no colored backgrounds) but consistent with KA brand: navy headings,
// gold accents, ink body. Section headers are gold smallcaps. Theme/kind
// labels are bracketed inline text like "[Training]" rather than colored
// pills.
//
// All three exports share a PDFDoc helper class that wraps jsPDF with the
// layout helpers we need (auto-pagination, headings, lists, footer).
// ═════════════════════════════════════════════════════════════════════════

// Letter page at jsPDF default unit ("pt"): 612 × 792
const PAGE_W = 612;
const PAGE_H = 792;
const MARGIN_L = 54;     // 0.75"
const MARGIN_R = 54;
const MARGIN_T = 54;
const MARGIN_B = 60;     // a bit more bottom for footer
const CONTENT_W = PAGE_W - MARGIN_L - MARGIN_R;  // 504

// Brand colors as [r,g,b] for jsPDF.
const C_NAVY = [35, 60, 95];
const C_RED  = [232, 53, 48];
const C_GOLD = [162, 145, 100];
const C_INK  = [28, 37, 48];
const C_MUTE = [107, 114, 128];
const C_LINE = [228, 224, 214];
const C_PAPER = [251, 250, 247];

// Helper class wrapping a jsPDF instance with our layout primitives.
class PDFDoc {
  constructor() {
    this.doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });
    this.y = MARGIN_T;
    this.doc.setFont("helvetica", "normal");
  }

  // Ensure there's at least `needed` pt of vertical space; new-page if not.
  ensureSpace(needed) {
    if (this.y + needed > PAGE_H - MARGIN_B) {
      this.doc.addPage();
      this.y = MARGIN_T;
    }
  }

  // Kicker line — small, gold, uppercase, letter-spaced.
  kicker(text) {
    this.ensureSpace(11);
    this.doc.setFont("helvetica", "bold");
    this.doc.setFontSize(8.5);
    this.doc.setTextColor(...C_GOLD);
    // Real letter-spacing via setCharSpace — tighter and less marquee-like
    // than inserting spaces between every character.
    this.doc.setCharSpace(1.6);
    this.doc.text(text.toUpperCase(), MARGIN_L, this.y);
    this.doc.setCharSpace(0);
    // 11pt isn't enough — the next call (title) renders at baseline, so its
    // glyph tops sit ABOVE the y coordinate, colliding with the kicker.
    // Need full clearance below the kicker baseline.
    this.y += 18;
  }

  // Main document title — serif, navy.
  title(text) {
    this.ensureSpace(24);
    this.doc.setFont("times", "bold");
    this.doc.setFontSize(18);
    this.doc.setTextColor(...C_NAVY);
    const lines = this.doc.splitTextToSize(text, CONTENT_W);
    lines.forEach((ln) => {
      this.doc.text(ln, MARGIN_L, this.y);
      this.y += 22;
    });
  }

  // Subtitle / metadata line under the title.
  subtitle(text) {
    this.ensureSpace(14);
    this.doc.setFont("helvetica", "normal");
    this.doc.setFontSize(10);
    this.doc.setTextColor(...C_MUTE);
    const lines = this.doc.splitTextToSize(text, CONTENT_W);
    lines.forEach((ln) => {
      this.doc.text(ln, MARGIN_L, this.y);
      this.y += 12;
    });
  }

  // Gold horizontal rule — used after the title block.
  rule() {
    this.ensureSpace(10);
    this.y += 4;
    this.doc.setDrawColor(...C_GOLD);
    this.doc.setLineWidth(1.2);
    this.doc.line(MARGIN_L, this.y, MARGIN_L + CONTENT_W, this.y);
    this.y += 8;
  }

  // Section heading — small caps, gold.
  section(text) {
    this.ensureSpace(20);
    this.y += 4;
    this.doc.setFont("helvetica", "bold");
    this.doc.setFontSize(9.5);
    this.doc.setTextColor(...C_GOLD);
    this.doc.setCharSpace(1.4);
    this.doc.text(text.toUpperCase(), MARGIN_L, this.y);
    this.doc.setCharSpace(0);
    this.y += 8;
    this.doc.setDrawColor(...C_LINE);
    this.doc.setLineWidth(0.5);
    this.doc.line(MARGIN_L, this.y, MARGIN_L + CONTENT_W, this.y);
    // Bigger gap below the underline — theme/step/group blocks render their
    // title at baseline, which sits BELOW the y coordinate. Without enough
    // gap the title visually crowds the underline.
    this.y += 14;
  }

  // Body paragraph — ink, justified-ish (jsPDF doesn't support justify
  // properly, so left-aligned with word-wrap).
  paragraph(text, opts = {}) {
    const size = opts.size || 11;
    const lineH = opts.lineH || 14;
    const color = opts.color || C_INK;
    const bold = !!opts.bold;
    this.doc.setFont("helvetica", bold ? "bold" : "normal");
    this.doc.setFontSize(size);
    this.doc.setTextColor(...color);
    const lines = this.doc.splitTextToSize(text, opts.width || CONTENT_W);
    for (const ln of lines) {
      this.ensureSpace(lineH);
      this.doc.text(ln, opts.x || MARGIN_L, this.y);
      this.y += lineH;
    }
  }

  // A theme block — name (bold navy), optional bracket label (gold), share
  // line (mute), description (ink). Atomic: never splits across a page.
  themeBlock({ title, label, share, description }) {
    // Pre-measure the whole block height so we can page-break BEFORE
    // starting rather than orphaning lines.
    let titleText = title;
    if (label) titleText += `  [${label}]`;
    this.doc.setFont("helvetica", "bold");
    this.doc.setFontSize(12);
    const titleLines = this.doc.splitTextToSize(titleText, CONTENT_W - 12);
    const titleH = titleLines.length * 15;
    const shareH = share ? 12 : 0;
    let descLines = [];
    if (description) {
      this.doc.setFont("helvetica", "normal");
      this.doc.setFontSize(10.5);
      descLines = this.doc.splitTextToSize(description, CONTENT_W - 12);
    }
    const descH = descLines.length * 13;
    const blockH = titleH + shareH + descH + 4;
    this.ensureSpace(blockH);

    // Gold left rule starts a touch above the title baseline.
    const startY = this.y - 4;
    // Title.
    this.doc.setFont("helvetica", "bold");
    this.doc.setFontSize(12);
    this.doc.setTextColor(...C_NAVY);
    for (const ln of titleLines) {
      this.doc.text(ln, MARGIN_L + 12, this.y);
      this.y += 15;
    }
    // Share line.
    if (share) {
      this.doc.setFont("helvetica", "bold");
      this.doc.setFontSize(8.5);
      this.doc.setTextColor(...C_MUTE);
      this.doc.text(share, MARGIN_L + 12, this.y);
      this.y += 12;
    }
    // Description.
    if (descLines.length) {
      this.doc.setFont("helvetica", "normal");
      this.doc.setFontSize(10.5);
      this.doc.setTextColor(...C_INK);
      for (const ln of descLines) {
        this.doc.text(ln, MARGIN_L + 12, this.y);
        this.y += 13;
      }
    }
    // Gold left rule for the whole block.
    const endY = this.y;
    this.doc.setDrawColor(...C_GOLD);
    this.doc.setLineWidth(2);
    this.doc.line(MARGIN_L, startY, MARGIN_L, endY - 4);
    this.y += 4;
  }

  // A numbered step (Where to start). Red circle with number, body to right.
  // Atomic: never splits across a page.
  stepBlock({ index, title, rationale, serves }) {
    // Pre-measure.
    const bodyW = CONTENT_W - 30;
    this.doc.setFont("helvetica", "bold");
    this.doc.setFontSize(11);
    const titleLines = this.doc.splitTextToSize(title, bodyW);
    this.doc.setFont("helvetica", "normal");
    this.doc.setFontSize(10);
    const ratLines = this.doc.splitTextToSize(rationale, bodyW);
    const titleH = titleLines.length * 14;
    const ratH = ratLines.length * 13;
    const servesH = serves ? 12 : 0;
    const circleR = 9;
    const circleSpace = circleR * 2 + 4;  // y must clear the circle
    const blockH = Math.max(titleH + ratH + servesH, circleSpace) + 6;
    this.ensureSpace(blockH);

    const startY = this.y;
    const circleX = MARGIN_L + 10;
    const circleY = this.y + 4;
    // Red circle.
    this.doc.setFillColor(...C_RED);
    this.doc.circle(circleX, circleY, circleR, "F");
    // Number inside circle, white.
    this.doc.setFont("helvetica", "bold");
    this.doc.setFontSize(10);
    this.doc.setTextColor(255, 255, 255);
    const numStr = String(index);
    const numW = this.doc.getTextWidth(numStr);
    this.doc.text(numStr, circleX - numW / 2, circleY + 3.5);
    // Body text.
    const bodyX = MARGIN_L + 30;
    // Title bold navy.
    this.doc.setFont("helvetica", "bold");
    this.doc.setFontSize(11);
    this.doc.setTextColor(...C_NAVY);
    for (const ln of titleLines) {
      this.doc.text(ln, bodyX, this.y);
      this.y += 14;
    }
    // Rationale.
    this.doc.setFont("helvetica", "normal");
    this.doc.setFontSize(10);
    this.doc.setTextColor(...C_INK);
    for (const ln of ratLines) {
      this.doc.text(ln, bodyX, this.y);
      this.y += 13;
    }
    // Serves line.
    if (serves) {
      this.doc.setFont("helvetica", "italic");
      this.doc.setFontSize(9);
      this.doc.setTextColor(...C_MUTE);
      this.doc.text(serves, bodyX, this.y);
      this.y += 12;
    }
    // Make sure y is below the circle.
    const minY = startY + circleSpace;
    if (this.y < minY) this.y = minY;
    this.y += 6;
  }

  // A grouping/intervention box — paper bg, line border.
  groupBlock({ heading, need, format }) {
    // Measure first so we can draw the background to the right size.
    const padX = 10;
    const padY = 9;
    const innerW = CONTENT_W - padX * 2;
    this.doc.setFontSize(10.5);
    this.doc.setFont("helvetica", "bold");
    const headingLines = this.doc.splitTextToSize(heading, innerW);
    this.doc.setFont("helvetica", "normal");
    this.doc.setFontSize(10);
    const needLines = this.doc.splitTextToSize(need, innerW);
    this.doc.setFont("helvetica", "bold");
    this.doc.setFontSize(9.5);
    const formatLines = this.doc.splitTextToSize(format, innerW);

    const blockH = padY * 2 + headingLines.length * 13 + needLines.length * 13 + formatLines.length * 12 + 6;
    this.ensureSpace(blockH + 4);

    const startY = this.y;
    // Background + border.
    this.doc.setFillColor(...C_PAPER);
    this.doc.setDrawColor(...C_LINE);
    this.doc.setLineWidth(0.6);
    this.doc.roundedRect(MARGIN_L, startY, CONTENT_W, blockH, 4, 4, "FD");

    let yy = startY + padY + 10;
    // Heading (group label) — bold navy.
    this.doc.setFont("helvetica", "bold");
    this.doc.setFontSize(10.5);
    this.doc.setTextColor(...C_NAVY);
    for (const ln of headingLines) {
      this.doc.text(ln, MARGIN_L + padX, yy);
      yy += 13;
    }
    // Need — ink.
    this.doc.setFont("helvetica", "normal");
    this.doc.setFontSize(10);
    this.doc.setTextColor(...C_INK);
    for (const ln of needLines) {
      this.doc.text(ln, MARGIN_L + padX, yy);
      yy += 13;
    }
    yy += 2;
    // Format — gold bold smaller.
    this.doc.setFont("helvetica", "bold");
    this.doc.setFontSize(9.5);
    this.doc.setTextColor(...C_GOLD);
    for (const ln of formatLines) {
      this.doc.text(ln, MARGIN_L + padX, yy);
      yy += 12;
    }
    this.y = startY + blockH + 8;
  }

  // Bullet list — used in per-department sections.
  bulletList(items) {
    this.doc.setFont("helvetica", "normal");
    this.doc.setFontSize(10.5);
    this.doc.setTextColor(...C_INK);
    for (const item of items) {
      const lines = this.doc.splitTextToSize(item, CONTENT_W - 14);
      this.ensureSpace(lines.length * 13);
      // Bullet
      this.doc.setFont("helvetica", "normal");
      this.doc.text("•", MARGIN_L + 2, this.y);
      for (let i = 0; i < lines.length; i++) {
        this.doc.text(lines[i], MARGIN_L + 14, this.y);
        this.y += 13;
      }
      this.y += 2;
    }
  }

  // Numbered list — used for recommended_next_steps.
  numberedList(items) {
    this.doc.setFont("helvetica", "normal");
    this.doc.setFontSize(10.5);
    this.doc.setTextColor(...C_INK);
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const lines = this.doc.splitTextToSize(item, CONTENT_W - 22);
      this.ensureSpace(lines.length * 13);
      this.doc.text(`${i + 1}.`, MARGIN_L + 2, this.y);
      for (let j = 0; j < lines.length; j++) {
        this.doc.text(lines[j], MARGIN_L + 22, this.y);
        this.y += 13;
      }
      this.y += 2;
    }
  }

  // Footer drawn on every page right before save.
  footerAllPages(text) {
    const total = this.doc.getNumberOfPages();
    for (let i = 1; i <= total; i++) {
      this.doc.setPage(i);
      this.doc.setDrawColor(...C_LINE);
      this.doc.setLineWidth(0.5);
      this.doc.line(MARGIN_L, PAGE_H - MARGIN_B + 14, MARGIN_L + CONTENT_W, PAGE_H - MARGIN_B + 14);
      this.doc.setFont("helvetica", "normal");
      this.doc.setFontSize(8);
      this.doc.setTextColor(...C_MUTE);
      const fullText = `${text}   ·   Page ${i} of ${total}`;
      const w = this.doc.getTextWidth(fullText);
      this.doc.text(fullText, (PAGE_W - w) / 2, PAGE_H - MARGIN_B + 28);
    }
  }

  save(filename) {
    this.doc.save(filename);
  }

  // Return the PDF as a Blob — used by the batch zip exporter.
  blob() {
    return this.doc.output("blob");
  }
}

// Build today's date in YYYY-MM-DD for filename suffixes.
function todayStamp() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// ── Per-department PDF — auto-downloaded ────────────────────────────────
// Build (but don't save) a per-department PDF. Returns the PDFDoc so the
// caller can either pdf.save(filename) for direct download or pdf.blob()
// for zipping into a batch archive.
function buildDepartmentPDF(dept, parent, pdInput) {
  const s = pdInput.summary || {};
  const conf = CONFIDENCE_STYLE[s.confidence] || CONFIDENCE_STYLE.mixed;

  const pdf = new PDFDoc();
  pdf.kicker("King's Academy · Department Review");
  pdf.title(dept.name);
  pdf.subtitle(
    `Submitted ${formatDate(parent.submittedAt)}   ·   ` +
    `Analysis generated ${formatDate(pdInput.generatedAt)}`
  );
  pdf.rule();

  if (s.overview) {
    pdf.section("Overview");
    pdf.paragraph(s.overview);
    pdf.y += 4;
    pdf.paragraph(`Department confidence: ${conf.label}`,
      { bold: true, size: 10, color: C_NAVY });
  }

  if (s.themes?.length) {
    pdf.section("Themes across the session");
    pdf.bulletList(s.themes.map((t) =>
      t.theme + (t.detail ? ` — ${t.detail}` : "")
    ));
  }

  if (s.capability_gaps?.length) {
    pdf.section("Capabilities they didn't know about");
    pdf.bulletList(s.capability_gaps.map((g) =>
      `${g.capability} — ${g.description}`
    ));
  }

  if (s.blockers?.length) {
    pdf.section("Blockers");
    pdf.bulletList(s.blockers.map((b) =>
      `[${BLOCKER_LABELS[b.type] || b.type}] ${b.description}`
    ));
  }

  if (s.pd_signals?.length) {
    pdf.section("PD signals");
    pdf.bulletList(s.pd_signals);
  }

  if (s.recommended_next_steps?.length) {
    pdf.section("Recommended next steps");
    pdf.numberedList(s.recommended_next_steps);
  }

  pdf.footerAllPages("King's Academy Faculty Tool Kit · k-a.app · admin-only document");
  return pdf;
}

function deptPDFFilename(dept) {
  return `Department-Review-${dept.name.replace(/\s+/g, "-")}-${todayStamp()}.pdf`;
}

async function exportDepartmentPDF(dept, parent, pdInput) {
  const pdf = buildDepartmentPDF(dept, parent, pdInput);
  pdf.save(deptPDFFilename(dept));
}

// ── Batch: zip up every department's full PDF ────────────────────────────
//
// Generates the full per-department PDF for every department that has an
// analysis available, bundles them into a single .zip, and triggers a
// download of that zip. Departments without an analysis are skipped (and
// their absence is reflected in the count returned).
//
// We re-fetch each parent review doc here rather than relying on the
// Rollup component's `data` (which doesn't include parent docs). The
// parent provides the submittedAt timestamp for the per-PDF subtitle.
async function exportAllDepartmentPDFsAsZip(data) {
  const zip = new JSZip();
  let included = 0;
  let skipped = 0;

  for (const { dept, pdInput } of data) {
    if (!pdInput?.summary) {
      skipped += 1;
      continue;
    }
    // Try to fetch the parent for the submittedAt date. If it fails or
    // is missing, fall back to a synthetic parent — the PDF still renders.
    let parent;
    try {
      const parentSnap = await getDoc(doc(db, "departmentReviews", dept.id));
      parent = parentSnap.exists() ? parentSnap.data() : { submittedAt: null };
    } catch (e) {
      console.warn(`Could not fetch parent for ${dept.id}:`, e);
      parent = { submittedAt: null };
    }
    const pdf = buildDepartmentPDF(dept, parent, pdInput);
    zip.file(deptPDFFilename(dept), pdf.blob());
    included += 1;
  }

  if (included === 0) {
    throw new Error("No department analyses available to include in the zip.");
  }

  // Generate the zip and trigger a download via a temporary anchor.
  const blob = await zip.generateAsync({ type: "blob" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Department-Reviews-All-${todayStamp()}.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Free the blob URL after the download is in flight.
  setTimeout(() => URL.revokeObjectURL(url), 1000);

  return { included, skipped };
}

// ── Per-department overviews — shared section renderer ─────────────────
//
// Renders the "By department" content into a PDFDoc instance: each
// department's name as a bold navy heading, followed by its one-paragraph
// overview (or a muted "(no analysis yet)" if none exists). Used in two
// places:
//   1. The standalone exportPerDepartmentSummariesPDF (just this section).
//   2. Prepended into exportRollupPDF before the cross-cutting synthesis,
//      so the full PDF includes per-department context.
//
// The "data" arg has the same shape as Rollup's data state:
//   [{ dept: { id, name }, pdInput: { summary: { overview, ... } } | null }]
function renderPerDepartmentSection(pdf, data) {
  pdf.section("By department");
  data.forEach(({ dept, pdInput }) => {
    const overview = pdInput?.summary?.overview || "";
    // We treat each (department name + overview) as one logical block.
    // The PDFDoc primitives don't have a direct "labeled paragraph with
    // gold left rule" helper, so we draw it as a themeBlock variant: bold
    // navy heading line, then the overview as body text, with the gold
    // left rule pulled from the existing themeBlock styling.
    pdf.themeBlock({
      title: dept.name,
      label: null,
      share: null,
      description: overview || "(no analysis yet)",
    });
  });
}

// ── Per-department summaries — standalone PDF ──────────────────────────
async function exportPerDepartmentSummariesPDF(data) {
  const pdf = new PDFDoc();
  pdf.kicker("King's Academy · Department Reviews");
  pdf.title("Department Overviews");
  pdf.subtitle("One-paragraph summary per department, before cross-department synthesis");
  pdf.rule();
  renderPerDepartmentSection(pdf, data);
  pdf.footerAllPages("King's Academy Faculty Tool Kit · k-a.app · admin-only document");
  pdf.save(`Department-Review-Overviews-${todayStamp()}.pdf`);
}

async function exportRollupPDF(report, data) {
  const pdf = new PDFDoc();
  pdf.kicker("King's Academy · Department Reviews");
  pdf.title("Cross-Department At-a-Glance");
  pdf.rule();

  // Per-department overviews come FIRST — readers get the source material
  // before the cross-department synthesis on top of it.
  if (data) renderPerDepartmentSection(pdf, data);

  pdf.section("Cross-cutting themes");
  (report.themes || []).forEach((t) => {
    const kindLabel = (THEME_KIND_STYLE[t.kind] || THEME_KIND_STYLE.mixed).label;
    pdf.themeBlock({
      title: t.theme,
      label: kindLabel,
      share: (t.departments || []).join(" · "),
      description: t.description,
    });
  });

  pdf.section("Where to start");
  (report.where_to_start || []).forEach((s, i) => {
    pdf.stepBlock({
      index: i + 1,
      title: s.step,
      rationale: s.rationale,
      serves: s.departments?.length ? `Serves: ${s.departments.join(", ")}` : null,
    });
  });

  pdf.section("Who needs what");
  (report.groupings || []).forEach((g) => {
    pdf.groupBlock({
      heading: formatGroup(g.departments ?? g.group),
      need: g.need,
      format: g.format,
    });
  });

  pdf.footerAllPages("King's Academy Faculty Tool Kit · k-a.app · admin-only document");
  pdf.save(`Department-Review-Full-${todayStamp()}.pdf`);
}

// ── Roll-up at-a-glance PDF — ANONYMIZED, share-out version ─────────────
//
// Same content as exportRollupPDF, but department names are replaced
// with percentages of the total (5 departments). Themes are sorted by
// share descending. Groupings collapse to "A group of N departments".
// The "Who needs what" section is renamed "Potential intervention
// strategies" in this share-out version.
async function exportAnonymizedRollupPDF(report) {
  const TOTAL = DEPARTMENTS.length;
  const pct = (n) => Math.round((n / TOTAL) * 100);

  // Sort themes by department count desc — most-shared first.
  const themesSorted = [...(report.themes || [])].sort(
    (a, b) => (b.departments?.length || 0) - (a.departments?.length || 0)
  );

  const pdf = new PDFDoc();
  pdf.kicker("King's Academy · Department Reviews");
  pdf.title("Cross-Department At-a-Glance");
  pdf.subtitle("Anonymized share-out · themes ordered by share of departments identifying them");
  pdf.rule();

  pdf.section("Cross-cutting themes");
  themesSorted.forEach((t) => {
    const kindLabel = (THEME_KIND_STYLE[t.kind] || THEME_KIND_STYLE.mixed).label;
    const n = (t.departments || []).length;
    pdf.themeBlock({
      title: t.theme,
      label: kindLabel,
      share: `Identified by ${pct(n)}% of departments`,
      description: t.description,
    });
  });

  pdf.section("Where to start");
  (report.where_to_start || []).forEach((s, i) => {
    const n = (s.departments || []).length;
    pdf.stepBlock({
      index: i + 1,
      title: s.step,
      rationale: s.rationale,
      serves: n > 0 ? `Serves ${pct(n)}% of departments` : null,
    });
  });

  pdf.section("Potential intervention strategies");
  (report.groupings || []).forEach((g) => {
    const depArr = Array.isArray(g.departments) ? g.departments
                 : Array.isArray(g.group) ? g.group
                 : typeof g.group === "string"
                   ? formatGroup(g.group).split(" · ").filter(Boolean)
                   : [];
    const n = depArr.length || 1;
    const label = n === 1 ? "A single department" : `A group of ${n} departments`;
    pdf.groupBlock({
      heading: label,
      need: g.need,
      format: g.format,
    });
  });

  pdf.footerAllPages(`King's Academy Faculty Tool Kit · k-a.app · share-out document · ${DEPARTMENTS.length} departments analyzed`);
  pdf.save(`Department-Review-Anonymized-${todayStamp()}.pdf`);
}

// ═════════════════════════════════════════════════════════════════════════
// PII AUDIT — superadmin only
// ═════════════════════════════════════════════════════════════════════════
// ═════════════════════════════════════════════════════════════════════════
// DEV TOOLS — for Kyle's test account only (devTools: true on user doc)
//
// Contains:
//   - Switch my own department (writes /users/{uid}.department directly,
//     so the live AuthContext onSnapshot re-routes the whole app within a
//     second or two without a refresh).
//   - PII audit log (relocated here from the admin home — it's a dev
//     thing, not a real admin feature).
//
// This view exists outside the normal product flow. Real admins do NOT
// see this section; the entry-point button is only rendered for users
// whose /users/{uid} doc has devTools: true (or who are superadmin).
// ═════════════════════════════════════════════════════════════════════════
function DevTools({ currentUid, currentDepartment, onBack, onOpenAudit, hodLanding }) {
  const [selected, setSelected] = useState(currentDepartment || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  // Reset-test-data state. Three stages:
  //   confirmReset === false  → button is idle
  //   confirmReset === 'ask'  → first modal asking "are you sure"
  //   confirmReset === 'type' → second modal requiring type-to-confirm
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetTypeText, setResetTypeText] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetLog, setResetLog] = useState([]);  // array of progress lines

  // Keep the dropdown synced if the live profile changes underneath us.
  useEffect(() => {
    setSelected(currentDepartment || "");
  }, [currentDepartment]);

  const applyDepartment = async () => {
    if (!currentUid) return;
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      await setDoc(
        doc(db, "users", currentUid),
        { department: selected || null },
        { merge: true }
      );
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } catch (e) {
      console.error("Department switch failed:", e);
      setError(e.message || "Write failed.");
    } finally {
      setSaving(false);
    }
  };

  // ── Reset test data ──────────────────────────────────────────────────
  //
  // Wipes everything the Department Review tool created: every department's
  // parent review doc + its segments subcollection, every pdInputs analysis,
  // the cross-department pdRollups doc, and (optionally) the PII audit log.
  //
  // Does NOT touch:
  //   - Storage audio files (live in audio-uploads/{uid}/... and are
  //     unrelated to the tool's app-state — they expire naturally or you
  //     can sweep them from the Storage console separately).
  //   - User docs / dev-tools flags / department assignments.
  //
  // Errors on individual deletes are logged but don't abort the sweep —
  // partial cleanup is better than abandoning halfway through.
  const performReset = async () => {
    setResetting(true);
    setResetLog([]);
    const log = (msg) => setResetLog((prev) => [...prev, msg]);

    let cleared = 0;
    let failed = 0;

    // 1. Each department: delete every segment doc, then the parent.
    for (const d of DEPARTMENTS) {
      try {
        const segSnap = await getDocs(
          collection(db, "departmentReviews", d.id, "segments")
        );
        for (const segDoc of segSnap.docs) {
          try {
            await deleteDoc(segDoc.ref);
            cleared += 1;
          } catch (e) {
            console.error("Segment delete failed:", e);
            failed += 1;
          }
        }
        await deleteDoc(doc(db, "departmentReviews", d.id));
        cleared += 1;
        log(`✓ Cleared ${d.name}`);
      } catch (e) {
        console.error(`Reset of ${d.id} failed:`, e);
        log(`✗ ${d.name} — ${e.message || "failed"}`);
        failed += 1;
      }
    }

    // 2. Each pdInputs analysis (deterministic IDs).
    for (const d of DEPARTMENTS) {
      try {
        await deleteDoc(doc(db, "pdInputs", `dept-${d.id}`));
        cleared += 1;
      } catch (e) {
        // Doc might not exist — that's fine, count as cleared.
        cleared += 1;
      }
    }
    log("✓ Cleared department analyses (pdInputs)");

    // 3. Cross-department synthesis.
    try {
      await deleteDoc(doc(db, "pdRollups", "current"));
      cleared += 1;
      log("✓ Cleared roll-up synthesis");
    } catch (e) {
      log("(no roll-up synthesis to clear)");
    }

    log(`Done. ${cleared} docs deleted, ${failed} errors.`);
    setResetting(false);
  };

  const closeResetModals = () => {
    setConfirmReset(false);
    setResetTypeText("");
    setResetLog([]);
  };

  return (
    <Shell>
      <Header
        title="Dev Tools"
        subtitle="Test-account utilities — not a real admin surface"
        right={onBack ? (
          <button onClick={onBack} style={ghostBtn}>← Back</button>
        ) : null}
      />

      {hodLanding && (
        <div style={{
          ...card, marginBottom: 16, background: "#FBF1DC",
          borderLeft: `3px solid ${KA.gold}`,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: KA.navy, marginBottom: 4 }}>
            No department set on this account
          </div>
          <div style={{ fontSize: 12.5, color: KA.ink, lineHeight: 1.55 }}>
            Pick a department below to enter the HOD session screen as that
            department's head.
          </div>
        </div>
      )}

      {/* Department switcher */}
      <div style={{ ...card }}>
        <SectionTitle>Switch my own department</SectionTitle>
        <div style={{ fontSize: 12.5, color: KA.mute, marginBottom: 12, lineHeight: 1.55 }}>
          Writes <code>department</code> on your own user doc. Within a
          second or two the rest of the app picks this up and the HOD
          session screen re-routes to the new department. No re-login
          needed.
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            style={{
              flex: 1, minWidth: 200, padding: "10px 12px", fontSize: 13,
              border: `1px solid ${KA.line}`, borderRadius: 8,
              background: "#fff", color: KA.ink, fontFamily: "inherit",
            }}
          >
            <option value="">(no department — clear field)</option>
            {DEPARTMENTS.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <button
            onClick={applyDepartment}
            disabled={saving || selected === (currentDepartment || "")}
            style={{
              ...primaryBtn,
              background: KA.navy,
              opacity: (saving || selected === (currentDepartment || "")) ? 0.5 : 1,
            }}
          >
            {saving ? "Saving…" : "Apply"}
          </button>
        </div>
        <div style={{ fontSize: 11, color: KA.mute, marginTop: 10 }}>
          Current: <strong style={{ color: KA.ink }}>{currentDepartment || "(none set)"}</strong>
        </div>
        {saved && (
          <div style={{
            marginTop: 10, fontSize: 12, color: "#1B7A3D",
            background: "#E7F3EC", borderRadius: 6, padding: "8px 10px",
          }}>
            ✓ Department updated. The app should re-route in a moment.
          </div>
        )}
        {error && (
          <div style={{
            marginTop: 10, fontSize: 12, color: KA.red,
            background: "#FDECEC", borderRadius: 6, padding: "8px 10px",
          }}>
            {error}
          </div>
        )}
      </div>

      {/* PII audit link */}
      <div style={{ ...card, marginTop: 16 }}>
        <SectionTitle>PII audit log</SectionTitle>
        <div style={{ fontSize: 12.5, color: KA.mute, marginBottom: 12, lineHeight: 1.55 }}>
          Every text payload sent to the LLM, original vs scrubbed. Use to
          monitor the scrubber's accuracy.
        </div>
        <button onClick={onOpenAudit} style={ghostBtn}>Open PII audit →</button>
      </div>

      {/* Reset test data — destructive, two-stage confirmation */}
      <div style={{
        ...card, marginTop: 16,
        border: `1px solid ${KA.red}`,
      }}>
        <div style={{
          fontSize: 12, fontWeight: 700, letterSpacing: "0.1em",
          textTransform: "uppercase", color: KA.red, marginBottom: 12,
        }}>
          ⚠ Reset test data
        </div>
        <div style={{ fontSize: 12.5, color: KA.mute, marginBottom: 12, lineHeight: 1.55 }}>
          Deletes every department's review submission, every AI analysis,
          the cross-department synthesis, and all segment content. Useful
          for clearing test runs between full rehearsals. Does NOT delete
          user accounts, dev-tools flags, or the PII audit log.
        </div>
        <button
          onClick={() => setConfirmReset("ask")}
          style={{
            background: "#fff", color: KA.red, border: `1px solid ${KA.red}`,
            borderRadius: 8, padding: "10px 16px", fontSize: 13,
            fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
          }}
        >
          Reset all test data…
        </button>
      </div>

      {/* First-stage modal — "are you sure" */}
      {confirmReset === "ask" && (
        <Modal onCancel={closeResetModals}>
          <div style={{ fontSize: 17, fontWeight: 700, color: KA.red, marginBottom: 10 }}>
            ⚠ This will delete all department review data
          </div>
          <div style={{ fontSize: 13, color: KA.ink, lineHeight: 1.6, marginBottom: 16 }}>
            All five departments' review submissions, every stored AI analysis,
            and the cross-department synthesis will be permanently deleted.
            This cannot be undone.
            <br /><br />
            Recordings already uploaded to Storage will remain; clean those
            up separately if needed.
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button onClick={closeResetModals} style={ghostBtn}>Cancel</button>
            <button
              onClick={() => setConfirmReset("type")}
              style={{
                background: KA.red, color: "#fff", border: "none",
                borderRadius: 8, padding: "11px 20px", fontSize: 14,
                fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
              }}
            >
              Continue
            </button>
          </div>
        </Modal>
      )}

      {/* Second-stage modal — type-to-confirm */}
      {confirmReset === "type" && (
        <Modal onCancel={resetting ? undefined : closeResetModals}>
          <div style={{ fontSize: 17, fontWeight: 700, color: KA.red, marginBottom: 10 }}>
            Type DELETE to confirm
          </div>
          <div style={{ fontSize: 13, color: KA.ink, lineHeight: 1.6, marginBottom: 14 }}>
            Type the word <strong>DELETE</strong> (all caps) in the box below
            to wipe all department review data.
          </div>
          <input
            type="text"
            value={resetTypeText}
            onChange={(e) => setResetTypeText(e.target.value)}
            disabled={resetting}
            placeholder="DELETE"
            autoFocus
            style={{
              width: "100%", boxSizing: "border-box",
              border: `1px solid ${KA.line}`, borderRadius: 8,
              padding: "10px 12px", fontSize: 14, fontFamily: "inherit",
              color: KA.ink, marginBottom: 14,
              letterSpacing: "0.05em",
            }}
          />

          {resetLog.length > 0 && (
            <div style={{
              background: KA.paper, border: `1px solid ${KA.line}`,
              borderRadius: 8, padding: "10px 12px", marginBottom: 14,
              maxHeight: 160, overflowY: "auto",
            }}>
              {resetLog.map((line, i) => (
                <div key={i} style={{
                  fontSize: 12, fontFamily: "'Courier New', monospace",
                  color: KA.ink, lineHeight: 1.55,
                }}>
                  {line}
                </div>
              ))}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button
              onClick={closeResetModals}
              disabled={resetting}
              style={{ ...ghostBtn, opacity: resetting ? 0.5 : 1 }}
            >
              {resetting ? "Working…" : "Cancel"}
            </button>
            <button
              onClick={performReset}
              disabled={resetting || resetTypeText !== "DELETE"}
              style={{
                background: KA.red, color: "#fff", border: "none",
                borderRadius: 8, padding: "11px 20px", fontSize: 14,
                fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                opacity: (resetting || resetTypeText !== "DELETE") ? 0.4 : 1,
              }}
            >
              {resetting ? "Deleting…" : "Delete everything"}
            </button>
          </div>
        </Modal>
      )}

      <div style={{
        marginTop: 22, fontSize: 11, color: KA.mute, lineHeight: 1.6,
        background: "#fff", border: `1px dashed ${KA.line}`, borderRadius: 8,
        padding: "10px 12px",
      }}>
        These tools are visible only to accounts with <code>devTools: true</code> on
        their user doc (and to superadmins). Set the flag manually in Firestore.
      </div>
    </Shell>
  );
}


// ═════════════════════════════════════════════════════════════════════════
// PII AUDIT — devTools or superadmin
// ═════════════════════════════════════════════════════════════════════════
function PIIAudit({ onBack }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openEntry, setOpenEntry] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(
          query(collection(db, "piiAuditLog"), orderBy("sentAt", "desc"))
        );
        const out = [];
        snap.forEach((d) => out.push({ id: d.id, ...d.data() }));
        setEntries(out);
      } catch (e) {
        console.error("Audit load failed:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <Shell>
      <Header
        title="PII Audit Log"
        subtitle="Developer-only · every text payload sent to the LLM, original vs scrubbed"
        right={<button onClick={onBack} style={ghostBtn}>← Admin home</button>}
      />

      {loading ? (
        <div style={{ ...card, textAlign: "center", color: KA.mute }}>Loading…</div>
      ) : entries.length === 0 ? (
        <div style={{ ...card, textAlign: "center", color: KA.mute }}>
          No audit entries yet. Submitting a department review or running an
          analysis produces entries here.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {entries.map((e) => (
            <button
              key={e.id}
              onClick={() => setOpenEntry(e)}
              style={{
                ...card, textAlign: "left", cursor: "pointer",
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}
            >
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: KA.navy }}>
                  {e.sourceRef || e.source || "(unknown source)"}
                </div>
                <div style={{ fontSize: 11, color: KA.mute, marginTop: 2 }}>
                  {formatDate(e.sentAt)}
                </div>
              </div>
              <span style={{ color: KA.gold, fontSize: 14 }}>view diff →</span>
            </button>
          ))}
        </div>
      )}

      {openEntry && (
        <Modal onCancel={() => setOpenEntry(null)} wide>
          <div style={{ fontSize: 15, fontWeight: 700, color: KA.navy, marginBottom: 4 }}>
            {openEntry.sourceRef || openEntry.source}
          </div>
          <div style={{ fontSize: 11, color: KA.mute, marginBottom: 14 }}>
            {formatDate(openEntry.sentAt)}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, maxHeight: "60vh", overflowY: "auto" }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: KA.gold, marginBottom: 6, letterSpacing: "0.08em" }}>
                ORIGINAL
              </div>
              <pre style={preStyle}>{openEntry.originalText || ""}</pre>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: KA.gold, marginBottom: 6, letterSpacing: "0.08em" }}>
                SCRUBBED (SENT TO LLM)
              </div>
              <pre style={{ ...preStyle, background: "#F0F5F0" }}>{openEntry.scrubbedText || ""}</pre>
            </div>
          </div>
          <div style={{ marginTop: 14, textAlign: "right" }}>
            <button onClick={() => setOpenEntry(null)} style={primaryBtn}>Close</button>
          </div>
        </Modal>
      )}
    </Shell>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Shared UI
// ═════════════════════════════════════════════════════════════════════════
function Shell({ children }) {
  return (
    <div style={{
      minHeight: "100vh", background: KA.paper,
      fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", color: KA.ink,
    }}>
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "32px 20px 64px" }}>
        {children}
      </div>
      <style>{`
        @keyframes drpulse { 0%,100%{opacity:1;transform:scale(1);} 50%{opacity:.5;transform:scale(.8);} }
        @keyframes drspin { to { transform: rotate(360deg); } }
        textarea:focus, input:focus { outline: none; border-color: ${KA.navy}; }
      `}</style>
    </div>
  );
}

function Header({ title, subtitle, right }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "flex-start",
      borderBottom: `2px solid ${KA.gold}`, paddingBottom: 14, marginBottom: 20,
    }}>
      <div>
        <div style={{ fontFamily: "Georgia, serif", fontSize: 22, fontWeight: 700, color: KA.navy }}>
          {title}
        </div>
        {subtitle && <div style={{ fontSize: 12.5, color: KA.mute, marginTop: 3 }}>{subtitle}</div>}
      </div>
      {right}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{
      fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase",
      color: KA.gold, marginBottom: 12,
    }}>{children}</div>
  );
}

function StepRail({ steps, current, segments, onJump, deptId }) {
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
      {steps.map((s, i) => {
        const seg = segments[s.id] || {};
        const isComplete = !!seg.completedAt;
        const hasContent = segmentHasContent(seg);
        const isCurrent = i === current;

        // Color logic:
        //   complete       → green
        //   current        → navy (overrides "has content" so it's obvious where they are)
        //   has content    → gold
        //   nothing yet    → line/gray
        const bg = isComplete ? "#1B7A3D"
                 : isCurrent  ? KA.navy
                 : hasContent ? KA.gold
                 : KA.line;
        const fg = (isComplete || isCurrent || hasContent) ? "#fff" : KA.mute;

        return (
          <button
            key={s.id}
            onClick={() => onJump(i)}
            title={`${s.title}${isComplete ? " — complete" : ""}`}
            style={{
              flex: 1, height: 36, minWidth: 36,
              borderRadius: 8, border: isCurrent ? `2px solid ${KA.navy}` : "1px solid transparent",
              cursor: "pointer",
              background: bg, color: fg,
              fontSize: 13, fontWeight: 700, fontFamily: "inherit",
              display: "flex", alignItems: "center", justifyContent: "center",
              gap: 4,
              transition: "background 0.15s",
            }}
          >
            {isComplete ? (
              <span style={{ fontSize: 16, lineHeight: 1 }}>✓</span>
            ) : (
              <span>{i + 1}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function ModeToggle({ mode, onChange }) {
  return (
    <div style={{
      display: "inline-flex", border: `1px solid ${KA.line}`, borderRadius: 8,
      overflow: "hidden", marginBottom: 12,
    }}>
      {[{ id: "type", label: "Type notes" }, { id: "record", label: "Record audio" }].map((o) => (
        <button
          key={o.id}
          onClick={() => onChange(o.id)}
          style={{
            fontSize: 13, padding: "8px 16px", border: "none", cursor: "pointer",
            background: mode === o.id ? KA.navy : "#fff",
            color: mode === o.id ? "#fff" : KA.ink,
            fontWeight: mode === o.id ? 700 : 400,
          }}
        >{o.label}</button>
      ))}
    </div>
  );
}

function SaveBadge({ state }) {
  if (state === "idle") return null;
  return (
    <span style={{ fontSize: 11, fontWeight: 700, color: state === "saving" ? KA.mute : "#1B7A3D" }}>
      {state === "saving" ? "Saving…" : "✓ Saved"}
    </span>
  );
}

function FacilitatorTip() {
  return (
    <div style={{
      marginTop: 22, fontSize: 12, color: KA.mute, lineHeight: 1.6,
      background: "#fff", border: `1px solid ${KA.line}`, borderRadius: 8, padding: "12px 14px",
    }}>
      <strong style={{ color: KA.navy }}>Facilitator tip:</strong> keep the
      discussion about practices, not people — "the department uses…" rather
      than naming individuals. Both typed notes and audio are saved.
    </div>
  );
}

function Modal({ onCancel, wide, children }) {
  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div style={{
        background: "#fff", borderRadius: 12, padding: "24px 26px",
        maxWidth: wide ? 900 : 520, width: "100%",
        boxShadow: "0 18px 60px rgba(0,0,0,0.3)",
      }}>
        {children}
      </div>
    </div>
  );
}

// ── Format a Firestore timestamp or Date as readable text ───────────────
function formatDate(v) {
  if (!v) return "—";
  let d;
  if (v.toDate) d = v.toDate();
  else if (v.seconds) d = new Date(v.seconds * 1000);
  else d = new Date(v);
  if (isNaN(d)) return "—";
  return d.toLocaleString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

// ── Style constants ─────────────────────────────────────────────────────
const card = {
  background: "#fff", border: `1px solid ${KA.line}`, borderRadius: 12, padding: 20,
};
const textareaStyle = {
  width: "100%", boxSizing: "border-box", resize: "vertical",
  border: `1px solid ${KA.line}`, borderRadius: 8, padding: "12px 14px",
  fontSize: 14, lineHeight: 1.6, fontFamily: "inherit", color: KA.ink,
};
const primaryBtn = {
  background: KA.red, color: "#fff", border: "none", borderRadius: 8,
  padding: "11px 20px", fontSize: 14, fontWeight: 700, cursor: "pointer",
  fontFamily: "inherit",
};
const ghostBtn = {
  background: "#fff", color: KA.navy, border: `1px solid ${KA.line}`,
  borderRadius: 8, padding: "11px 18px", fontSize: 14, cursor: "pointer",
  fontFamily: "inherit", fontWeight: 600,
};
const preStyle = {
  background: KA.paper, border: `1px solid ${KA.line}`, borderRadius: 8,
  padding: "10px 12px", fontSize: 12, lineHeight: 1.5, color: KA.ink,
  fontFamily: "'Courier New', monospace",
  whiteSpace: "pre-wrap", wordBreak: "break-word",
  minHeight: 200,
};